import {
  categoryIndicators,
  formatRiskPointChange,
  mapRiskGroupName,
  mapRiskLostReasonName,
  mapRiskName,
  riskIndicatorGroupings,
  type RiskCategory,
  type RiskIndicator,
  type RiskIndicatorGroup,
} from "./risk-contracts";
import {
  getStageLabel,
  getPipelineLabel,
  getRevelateStageProbabilityForDeal,
  getStageProbability,
  findNumStageRegressions,
} from "./pipelines";
import { Database, NestedCompany, NestedCurrency, TODO } from "@revelate/types";
import { getDealValueInCompanyCurrency } from "./currencies";
export { categoryIndicators, type RiskIndicatorsInCategory } from "./risk-contracts";
import {
  addBusinessDays,
  addDays,
  // addDays,
  addMonths,
  addQuarters,
  differenceInCalendarDays,
  differenceInMilliseconds,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  formatDuration,
  intervalToDuration,
  isAfter,
  isBefore,
  isWeekend,
  isWithinInterval,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
} from "date-fns";
import { getCompanyDomain } from "./companies";
import { clamp, getFullName } from "@revelate/utils";
import { getDealOwner } from "@revelate/utils";
import { getHistoricalPropertyAtSnapshotDate } from "../lib/history";
import { buildRiskHistoryDealSnapshot } from "./risk-history";

const DEALS_TABLE_COUNT = 5;
const AVG_NUM_MEETINGS = 1;

// Important dates
const now = new Date();
const startOfThisMonth = startOfMonth(now);
const endOfThisMonth = endOfMonth(now);
const startOfThisQuarter = startOfQuarter(now);
const endOfThisQuarter = endOfQuarter(now);
const startOfLastMonth = startOfMonth(subMonths(now, 1));
const swedenHolidayPeriodStart = new Date(now.getFullYear(), 6, 13); // July 13
const swedenHolidayPeriodEnd = new Date(now.getFullYear(), 7, 9); // July 13
const missedCloseDateCutoff = subWeeks(now, 6);

type DataBreakdown =
  | "by_pipeline"
  // | "by_product"
  | "by_month"
  | "by_sales_rep"
  // | "by_segment"
  ;

export type ForecastTimePeriod =
  | "this_month"
  | "next_month"
  | "prev_quarter"
  | "this_quarter"
  | "next_quarter"
  | "next_6_months"
  | "this_year"
  ;

export function getForecastTimePeriodRange(
  forecastTimePeriod: ForecastTimePeriod,
  referenceDate = now,
) {
  if (forecastTimePeriod === "this_month" || forecastTimePeriod === "next_month") {
    const month = forecastTimePeriod === "next_month" ? addMonths(referenceDate, 1) : referenceDate;
    return { start: startOfMonth(month), end: endOfMonth(month) };
  }
  const referenceStartOfQuarter = startOfQuarter(referenceDate);
  const referenceStartOfNextQuarter = startOfQuarter(addQuarters(referenceDate, 1));
  const referenceStartOfPrevQuarter = startOfQuarter(subQuarters(referenceDate, 1));
  if (forecastTimePeriod === "prev_quarter") {
    return {
      start: referenceStartOfPrevQuarter,
      end: endOfQuarter(referenceStartOfPrevQuarter),
    };
  }
  if (forecastTimePeriod === "next_quarter") {
    return {
      start: referenceStartOfNextQuarter,
      end: endOfQuarter(referenceStartOfNextQuarter),
    };
  }
  if (forecastTimePeriod === "next_6_months") {
    return {
      start: startOfMonth(referenceDate),
      end: endOfMonth(addMonths(referenceDate, 5)),
    };
  }
  if (forecastTimePeriod === "this_year") {
    return {
      start: startOfYear(referenceDate),
      end: endOfYear(referenceDate),
    };
  }
  return {
    start: referenceStartOfQuarter,
    end: endOfQuarter(referenceStartOfQuarter),
  };
}

type ManagementReviewPeriodRange = {
  from: Date;
  to: Date;
};

export type ManagementReviewPipelineMovementRow = {
  type: "start" | "positive" | "negative" | "end";
  label: string;
  value: number;
};

export type ManagementReviewWinLossFlowRow = {
  source: string;
  outcome: "won" | "lost";
  theme: string;
  value: number;
};

export type ManagementReviewUrgencyRisk = TODO & {
  urgency_score: number;
  urgency_bucket: "high" | "medium" | "low";
};

export type StateOfSalesV2LossSignalGroup = {
  group: Exclude<RiskIndicatorGroup, "portfolioRisk">;
  label: string;
  affectedDealCount: number;
  historicallyEvaluatedDealCount: number;
  missingHistoricalDealCount: number;
  recentLostDealCount: number;
  deals: Array<TODO & {
    lossSignalIndicators: Array<{
      indicator: RiskIndicator;
      score: number;
    }>;
  }>;
};

export type StateOfSalesV2ForecastMovementCategoryKey =
  | "new_deals"
  | "closed_won"
  | "closed_lost"
  | "moved_in"
  | "moved_out"
  | "improved_outlook"
  | "weakened_outlook"
  | "unclassified";

export type StateOfSalesV2ForecastMovementDeal = {
  id: string;
  name: string;
  deal: TODO;
  forecastImpact: number;
  previousForecastContribution: number | null;
  currentForecastContribution: number | null;
  previousRisk: number | null;
  currentRisk: number | null;
  changedIndicators: Array<{
    indicator: RiskIndicator;
    label: string;
    previousRisk: number;
    currentRisk: number;
  }>;
  indicatorHistoryAvailable: boolean;
  ownerName: string;
  createdOrReopenedAt: string | null;
  currentStage: string | null;
  previousStage: string | null;
  previousExpectedClose: string | null;
  currentExpectedClose: string | null;
  actualCloseDate: string | null;
  includedInStartForecast: boolean;
  changeSummary: string | null;
  changeObservedAt: string | null;
  comparisonPeriodStart: string;
  comparisonPeriodEnd: string;
  missingHistoricalInput: string | null;
};

export type StateOfSalesV2ForecastMovement = {
  asOf: string;
  available: boolean;
  startForecast: number;
  expectedFromOpenDeals: number;
  won: number;
  values: {
    newDeals: number;
    closedWon: number;
    closedLost: number;
    movedIn: number;
    movedOut: number;
    improvedOutlook: number;
    weakenedOutlook: number;
  };
  dealsByCategory: Record<
    StateOfSalesV2ForecastMovementCategoryKey,
    StateOfSalesV2ForecastMovementDeal[]
  >;
  riskFallbackDealCount: number;
};

export function getRiskWeight(
  indicator: RiskIndicator,
  company_id: number | null | undefined,
): number {
  // CASE: "Two" only
  if (company_id === 49) {
    switch (indicator) {
      case "stageProgression": return 2.41;
      case "closeDateInPast": return 2.73;
      case "closeDateSlippage": return 2.68;
      case "lastModifiedDate": return 2.79;
      case "dependsOnFewLargeDeals": return 3;
      case "lastActivityTooLongAgo": return 2.84;
      case "noNextStepAssigned": return 3;
      case "nextActivityUnscheduled": return 2;
      case "stalled": return 3;
      case "toneOfVoice": return 1.72;
      case "emailResponseTime": return 2.75;
      case "stakeholderInvolvement": return 3;
      case "stageRegression": return 2.82;
      case "probabilityInflation": return 2;
      case "emailRatio": return 2.87;
      case "regression": return 1;
      case "weightedValueOverBudget": return 2;
      case "salesTarget": return 2;
      case "pipelineFilled": return 2;
      case "numberOfMeetings": return 3;
      case "leadSource": return 2;
      case "netRevenue": return 2;
      case "totalTimeToClose": return 2;
      default: return 0;
    }
  }
  // CASE: "IT Software" only
  if (company_id == 53) {
    switch (indicator) {
      case "stageProgression": return 1.98;
      case "closeDateInPast": return 1.82;
      case "closeDateSlippage": return 2.01;
      case "lastModifiedDate": return 1.94;
      case "dependsOnFewLargeDeals": return 3;
      case "lastActivityTooLongAgo": return 1.87;
      case "noNextStepAssigned": return 3;
      case "nextActivityUnscheduled": return 2;
      case "stalled": return 3;
      case "toneOfVoice": return 1.72;
      case "emailResponseTime": return 1.42;
      case "stakeholderInvolvement": return 3;
      case "stageRegression": return 2.90;
      case "probabilityInflation": return 2;
      case "emailRatio": return 2.87; // Global
      case "regression": return 1;
      case "weightedValueOverBudget": return 2;
      case "salesTarget": return 2;
      case "pipelineFilled": return 2;
      case "numberOfMeetings": return 3;
      case "leadSource": return 2;
      case "netRevenue": return 2;
      case "totalTimeToClose": return 2;
      default: return 0;
    }
  }
  // CASE: Global but trained only on tendium. Needs retraining
  else {
    switch (indicator) {
      case "stageProgression": return 1.89;
      case "closeDateInPast": return 2.54;
      case "closeDateSlippage": return 2.73;
      case "lastModifiedDate": return 2.78;
      case "dependsOnFewLargeDeals": return 3;
      case "lastActivityTooLongAgo": return 2.57;
      case "noNextStepAssigned": return 3;
      case "nextActivityUnscheduled": return 2;
      case "stalled": return 3;
      case "toneOfVoice": return 1.72;
      case "emailResponseTime": return 1.56;
      case "stakeholderInvolvement": return 3;
      case "stageRegression": return 2.92;
      case "probabilityInflation": return 2;
      case "emailRatio": return 2.87;
      case "regression": return 1;
      case "weightedValueOverBudget": return 2;
      case "salesTarget": return 2;
      case "pipelineFilled": return 2;
      case "numberOfMeetings": return 3;
      case "leadSource": return 2;
      case "netRevenue": return 2;
      case "totalTimeToClose": return 2;
      default: return 0;
    }
  }
}

export function isRiskIndicatorWeightML(indicator: RiskIndicator) {
  switch (indicator) {
    case "closeDateSlippage":
    case "stageProgression":
    case "lastModifiedDate":
    case "lastActivityTooLongAgo":
    case "closeDateInPast":
    case "stageRegression":
    case "emailRatio":
    case "emailResponseTime":
    case "toneOfVoice":
      return true;
    default:
      return false;
  }
}

export const riskBadgeVariants = {
  high: "rejected",
  medium: "approved",
  low: "paid",
};

export function getRiskBucket(score: number) {
  if (score >= 50) return "high";
  if (score >= 25) return "medium";
  return "low";
}

export function formatCurrencyValue(
  value: number,
  companyCurrency: Database["public"]["Tables"]["currencies"]["Row"] | null,
  decimalPlaces = 0,
) {
  const isNegative = value < 0;
  const absoluteValue = Math.abs(value);

  let formattedValue =
    absoluteValue >= 1_000_000
      ? `${(absoluteValue / 1_000_000).toFixed(decimalPlaces)}M`
      : absoluteValue >= 1_000
        ? `${(absoluteValue / 1_000).toFixed(decimalPlaces)}K`
        : absoluteValue === 0
          ? absoluteValue.toFixed(0)
          : absoluteValue.toFixed(decimalPlaces);

  if (isNegative) {
    formattedValue = `-${formattedValue}`
  }

  return `${formattedValue} ${companyCurrency?.code ?? "SEK"}`;
}

function getRemainingTimeToCloseInMs(
  deal: any,
  pipelines: any[],
  dealstageAvgTimes: any[] | undefined,
  asOf: Date = now,
) {
  if (dealstageAvgTimes === undefined || !dealstageAvgTimes?.length) return 0;
  if (!deal.pipeline || !pipelines?.length) return 0;

  const stagesInPipeline = pipelines
    ?.find((p: any) => p.id === deal.pipeline)
    ?.stages
    ?.filter((s: any) => Number(s.metadata.probability) > 0 && Number(s.metadata.probability) < 1)
    ?.toSorted((a: any, b: any) => Number(a.metadata.probability) - (b.metadata.probability));

  const stagesWithTimesMs = stagesInPipeline
    ?.map((s: any) => ({
      stage: s.id,
      label: s.label,
      probability: Number(s.metadata.probability),
      milliseconds: dealstageAvgTimes?.find(dsat => dsat.stage === s.id)?.milliseconds ?? 0,
    }))
    ?.filter((s: any) => s.probability < 1) as any[] ?? [];

  const currentStage = deal.provider_data?.properties?.dealstage;
  const historyCurrentStage = currentStage
    ? deal.provider_data?.propertiesWithHistory?.dealstage?.find((ds: any) => ds.value === currentStage)
    : null;
  const timeSpentInCurrentStageMs = historyCurrentStage
    ? differenceInMilliseconds(asOf, historyCurrentStage.timestamp)
    : null;

  const currentStageIdx = stagesWithTimesMs.findIndex(st => st.stage === deal.provider_data?.properties?.dealstage);
  const currentStageAvgTimeMs = stagesWithTimesMs.find(st => st.stage === deal.provider_data?.properties?.dealstage)?.milliseconds ?? 0;
  const futureStagesWithTimes = stagesWithTimesMs.slice(currentStageIdx);

  const remainingTimeInCurrentStageMs = timeSpentInCurrentStageMs
    ? Math.max(0, currentStageAvgTimeMs - timeSpentInCurrentStageMs)
    // ? currentStageAvgTimeMs - timeSpentInCurrentStageMs
    : 0;
  const remainingTimeInFutureStagesMs = futureStagesWithTimes?.reduce((acc, stageWithTime) => acc + stageWithTime.milliseconds, 0);
  const totalRemainingTimeMs = remainingTimeInCurrentStageMs + remainingTimeInFutureStagesMs;

  const result = totalRemainingTimeMs ?? currentStageAvgTimeMs ?? 0;
  return result;
}

export function getRevelateExpectedCloseDate(
  deal: any,
  pipelines: any[],
  dealstageAvgTimes: any[] | undefined,
  asOf: Date = now,
) {
  const crmCloseDate = deal.closed_at ? new Date(deal.closed_at) : null;
  if (!crmCloseDate || Number.isNaN(crmCloseDate.getTime())) return null;

  const remainingStagesTimeMs = getRemainingTimeToCloseInMs(
    deal,
    pipelines,
    dealstageAvgTimes,
    asOf,
  );
  let adjustedCloseDateBasedOnAvgStageTimes = !Number.isNaN(remainingStagesTimeMs)
    && remainingStagesTimeMs
    ? new Date(asOf.getTime() + remainingStagesTimeMs)
    : crmCloseDate;

  if (isWeekend(adjustedCloseDateBasedOnAvgStageTimes)) {
    adjustedCloseDateBasedOnAvgStageTimes = addBusinessDays(
      adjustedCloseDateBasedOnAvgStageTimes,
      1,
    );
  }

  if (isWithinInterval(adjustedCloseDateBasedOnAvgStageTimes, {
    start: swedenHolidayPeriodStart,
    end: swedenHolidayPeriodEnd,
  })) {
    adjustedCloseDateBasedOnAvgStageTimes = addBusinessDays(
      swedenHolidayPeriodEnd,
      1,
    );
  }

  return isBefore(adjustedCloseDateBasedOnAvgStageTimes, crmCloseDate)
    ? crmCloseDate
    : adjustedCloseDateBasedOnAvgStageTimes;
}

export function prepareDealsWithCurrencyConversion(
  deals: any[] | undefined,
  currencies: NestedCurrency[],
  company: NestedCompany,
) {
  if (!deals || !deals.length) return [];

  const parsedDeals = deals
    .map(d => ({
      ...d,
      provider_data: JSON.parse(d.provider_data as string),
      // Override "value" field with company currency converted value
      value: getDealValueInCompanyCurrency(d, currencies, company),
      // And preserve the deal currency value in new field
      valueInDealCurrency: d.value,
    }))

  return parsedDeals;
}

export function prepareDealsForRiskAssessment(
  deals: any[] | undefined,
  pipelines: any[],
  currencies: NestedCurrency[],
  company: NestedCompany,
  dealstageAvgTimes: any[] | undefined,
  options: { preserveOverdueDealIds?: ReadonlySet<number> } = {},
) {
  if (!deals || !deals.length) return [];
  const pipelineResults = (pipelines as any)?.results ?? [];

  // Exclude churn pipelines
  let pipelinesToExclude: string[] = []

  if (company.id === 1) {
    pipelinesToExclude = [
      "505800656",
      // "30629415",
      "506608832",
      // "1093458127",
      "1217877208",
    ];
  }

  const filteredDeals = deals
    .filter(d => !pipelinesToExclude.includes(d.pipeline))
    .filter(d => {
      if (d.is_closed) return true;
      // Only the canonical V2 caller opts supported Deals out of the V1 cutoff.
      if (options.preserveOverdueDealIds?.has(Number(d.id))) return true;
      return !isBefore(d.closed_at, missedCloseDateCutoff)
    });

  const parsedDeals = filteredDeals
    .map(d => ({
      ...d,
      provider_data: JSON.parse(d.provider_data as string),
      // Provide empty risks key to be populated
      risks: {},
      // Override "value" field with company currency converted value
      value: getDealValueInCompanyCurrency(d, currencies, company),
      // And preserve the deal currency value in new field
      valueInDealCurrency: d.value,
    }))
    .map(d => ({
      ...d,
      owner_name: getDealOwner(d) ? getFullName(getDealOwner(d)) : "No owner",
      // @ts-ignore
      stage_label: getStageLabel(pipelineResults, d.pipeline, d.provider_data?.properties?.dealstage ?? ""),
      // @ts-ignore
      stage_probability: getStageProbability(pipelineResults, d.pipeline, d.provider_data?.properties?.dealstage ?? ""),
      // @ts-ignore
      stage_probability_adjusted: getStageProbability(pipelineResults, d.pipeline, d.provider_data?.properties?.dealstage ?? "", true),
      // @ts-ignore
      pipeline_label: getPipelineLabel(pipelineResults, d.pipeline),
      // @ts-ignore
      remainingStagesTimeMs: getRemainingTimeToCloseInMs(
        d,
        pipelineResults,
        dealstageAvgTimes,
      ),
      msUntilCloseDate: differenceInMilliseconds(d.closed_at, now),
    }))
    .map(d => {
      const adjustedCloseDate = getRevelateExpectedCloseDate(
        d,
        pipelineResults,
        dealstageAvgTimes,
      );

      return {
        ...d,
        adjustedCloseDate,
      }
    })

  // const remainingTimeDiffs = parsedDeals
  //   .map(d => d.remainingStagesTimeMs - d.msUntilCloseDate)
  //   .filter(v => !isNaN(v));
  // const totalDiffMs = remainingTimeDiffs.reduce((acc, val) => acc + val, 0);
  // const meanDiffMs = totalDiffMs / remainingTimeDiffs.length;
  // console.log("meanDiffMs", meanDiffMs, "meanDiffDays", millisecondsToDays(meanDiffMs));

  return parsedDeals;
}

// function millisecondsToDays(ms: number) {
//   return ms / (24 * 60 * 60 * 1000);
// }

export function evaluateRiskPipelineRisk(
  hubspotDeals: any[],
  companyId: number | null | undefined,
  pipelines: any[],
  dataBreakdown: DataBreakdown,
  selectedPipeline: string,
  selectedOwner: string,
  bubbleChartView: "largest" | "riskiest",
  dealStageAvgTimes: any[] | undefined,
  _companyCurrency: any | null,
) {
  const filteredDeals = hubspotDeals.filter(deal => {
    if (selectedPipeline !== "all" && deal.pipeline !== selectedPipeline) return false;
    if (selectedOwner !== "all" && getDealOwner(deal) === null) return false;
    if (selectedOwner !== "all" && getDealOwner(deal)?.id !== selectedOwner) return false;
    return true;
  })

  const totalOpenDealValue = filteredDeals
    .map(d => d.value)
    .reduce((acc, val) => acc + val, 0);

  let deals = filteredDeals.map(d => {
    const concentrationPct = totalOpenDealValue === 0
      ? 0
      : d.value * 100 / totalOpenDealValue;

    // NOTE: Setting to full 10 to avoid taking this factor into account
    const dealValueAsPctOfTotalValue = 10;

    const stageProbability = getRevelateStageProbabilityForDeal(pipelines, d);

    const owner = d.users?.find((u: any) => u.role === "owner");
    const ownerId = owner ? owner.data.id : undefined;

    return {
      ...d,
      ownerId,
      percentage_of_total_value: dealValueAsPctOfTotalValue,
      concentrationPct,
      deal_scale: dealValueAsPctOfTotalValue,
      stage_scale: stageProbability,
    };
  })

  // Pipeline concentration
  // What % of deals take up half the value of the pipeline
  // FIXME: Use a different technique to get the exact threshold instead of stepping through 
  // discrete points. Just sort by largest deals, then keep adding until it crosses half the total
  // value, then find what percentage that count is

  const pipelineConcentrationThresholdDealValue = totalOpenDealValue * 0.50;
  const numDeals = deals.length ?? 0;

  const pipelineConcentrationDealCountThresholdPcts = [0.05, 0.10, 0.15, 0.20, 0.25, 0.30, 0.40, 0.50, 0.60, 0.70, 0.80, 0.90];
  const openDealsSortedByValue = deals
    .toSorted((a, b) => b.value - a.value);

  let pipelineConcentrationThresholdMet = 100;
  let pipelineConcentrationDealQuantity = 0;
  const pipelineConcentrationDealQuantityMaxThreshold = 10; // num deals
  let concentratedDeals: any[] = [];

  // Loop through increasing thresholds and check where pipeline concentration occurs
  for (const dealCountThresholdPct of pipelineConcentrationDealCountThresholdPcts) {
    const pipelineConcentrationDealCountThreshold = Math.ceil(numDeals * dealCountThresholdPct);

    const dealsInPipelineConcentration = openDealsSortedByValue
      .slice(0, pipelineConcentrationDealCountThreshold);

    const totalValueOfConcentratedDeals = dealsInPipelineConcentration
      .map(d => d.value)
      .reduce((acc, val) => acc + val, 0);

    if (totalValueOfConcentratedDeals > pipelineConcentrationThresholdDealValue) {
      concentratedDeals = [...dealsInPipelineConcentration];
      pipelineConcentrationThresholdMet = dealCountThresholdPct * 100;
      pipelineConcentrationDealQuantity = dealsInPipelineConcentration.length;

      const concentrationDealIds = dealsInPipelineConcentration.map(d => d.id);
      deals = deals.map(d => {
        if (!concentrationDealIds.includes(d.id)) return d;
        return {
          ...d,
          pipelineConcentration: dealCountThresholdPct * 100,
        }
      })

      break;
    }
  }

  const isPipelineConcentrationHigh =
    pipelineConcentrationThresholdMet <= 20 &&
    pipelineConcentrationDealQuantity <= pipelineConcentrationDealQuantityMaxThreshold;

  const pipelineConcentrationSummary = isPipelineConcentrationHigh
    ? `deals represent half the pipeline value. Pipeline concentration is high. These deals materially impact quarter outcomes.`
    : `deals represent half the pipeline value. Pipeline concentration is balanced. No single deal materially impacts quarter outcomes.`

  const pipelineConcentrationShortSummary = isPipelineConcentrationHigh
    ? "high"
    : "balanced";

  // Evaluate risks

  const dealsWithRiskIndicatorsEvaluated = evaluateRiskIndicators(
    deals,
    companyId,
    pipelines,
    dealStageAvgTimes,
  );

  const dealsWithRiskScores = dealsWithRiskIndicatorsEvaluated
    .map(d => ({
      ...d,
      risk_score: getTotalDealRiskScoreInCategory(d, "forecast"),
      weighted_risk_score: getWeightedDealRiskScoreInCategory(d, "forecast"),
      pipeline_risk_score: getWeightedDealRiskScoreInCategory(d, "pipelineRisk"),
    }))
    .toSorted((a, b) => b.pipeline_risk_score - a.pipeline_risk_score);

  const earlyStageRiskyDeals = dealsWithRiskScores
    .filter(d => d.stage_scale <= 0.2);

  const earlyStageRiskyDealsTotalValue = earlyStageRiskyDeals
    .map(d => d.value)
    .reduce((acc, val) => acc + val, 0);

  const lateStageRiskyDeals = dealsWithRiskScores
    .filter(d => d.stage_scale > 0.8);

  const lateStageRiskyDealsTotalValue = lateStageRiskyDeals
    .map(d => d.value)
    .reduce((acc, val) => acc + val, 0);

  const lateStageHasHigherExposure = lateStageRiskyDealsTotalValue > earlyStageRiskyDealsTotalValue;

  let riskExposureDeals = lateStageHasHigherExposure
    ? lateStageRiskyDeals
    : earlyStageRiskyDeals;

  riskExposureDeals = riskExposureDeals
    .toSorted((a, b) => b.weighted_risk_score - a.weighted_risk_score)
    .slice(0, 10)
    .map(d => ({
      ...d,
      topRisk: getDealTopRiskInCateogory(d, "pipelineRisk"),
    }))

  const riskBreakdownChartData =
    formatDealsForPipelineRiskChart(
      dealsWithRiskScores,
      dataBreakdown,
    );

  const lowRiskDeals = dealsWithRiskScores
    .filter(d => d.weighted_risk_score < 25);

  const lowRiskDealsTotalValue = lowRiskDeals
    .map(d => d.value)
    .reduce((acc, val) => acc + val, 0);

  const lowRiskDealsTotalValuePctOfAll = totalOpenDealValue === 0
    ? 0
    : lowRiskDealsTotalValue * 100 / totalOpenDealValue;

  const lowRiskDealsRiskAdjustedTotalValue = lowRiskDeals
    .map(d => d.value * d.risk_score / 100)
    .reduce((acc, val) => acc + val, 0);

  const mediumRiskDeals = dealsWithRiskScores
    .filter(d => d.weighted_risk_score >= 25 && d.weighted_risk_score < 50);

  const mediumRiskDealsTotalValue = mediumRiskDeals
    .map(d => d.value)
    .reduce((acc, val) => acc + val, 0);

  const mediumRiskDealsTotalValuePctOfAll = totalOpenDealValue === 0
    ? 0
    : mediumRiskDealsTotalValue * 100 / totalOpenDealValue;

  const mediumRiskDealsRiskAdjustedTotalValue = mediumRiskDeals
    .map(d => d.value * d.risk_score / 100)
    .reduce((acc, val) => acc + val, 0);

  const highRiskDeals = dealsWithRiskScores
    .filter(d => d.weighted_risk_score >= 50);

  const highRiskDealsTotalValue = highRiskDeals
    .map(d => d.value)
    .reduce((acc, val) => acc + val, 0);

  const riskExposureSummaryByStage = highRiskDealsTotalValue === 0
    ? "No high risk deals in this time period."
    : lateStageHasHigherExposure
      ? "Revenue exposure is concentrated in late stage deals."
      : "Revenue exposure is concentrated in early stage deals.";

  const highRiskDealsTotalValuePctOfAll = totalOpenDealValue === 0
    ? 0
    : highRiskDealsTotalValue * 100 / totalOpenDealValue;

  const highRiskDealsRiskAdjustedTotalValue = highRiskDeals
    .map(d => d.value * d.risk_score / 100)
    .reduce((acc, val) => acc + val, 0);

  const dealsForBubbleChartBySize = dealsWithRiskScores
    .toSorted((a, b) => b.value - a.value)
    .slice(0, 10);

  const dealsForBubbleChartByRisk = dealsWithRiskScores
    // .filter(d => d.risk_score >= 25)
    .toSorted((a, b) => b.weighted_risk_score - a.weighted_risk_score)
    .slice(0, 10);

  const dealsForBubbleChart = bubbleChartView === "riskiest"
    ? dealsForBubbleChartByRisk
    : dealsForBubbleChartBySize;

  const bubbleChartData = dealsForBubbleChart
    .map(d => ({
      id: d.id,
      name: d.name,
      owner_name: d.owner_name,
      value: d.value,
      risk_score: d.weighted_risk_score,
      close_probability: d.stage_probability,
      stage_probability: d.stage_probability_adjusted * 100,
    }))

  const dealsAtRisk = dealsWithRiskScores
    .filter(d => Object.keys(d.risks).length > 0)

  const dealsAtRiskSortedBySize = dealsAtRisk
    .toSorted((a, b) => b.value - a.value);

  const largestAtRiskDeal = dealsAtRiskSortedBySize.length
    ? dealsAtRiskSortedBySize[0]
    : null;

  const topDealsAtRisk = dealsAtRisk.slice(0, DEALS_TABLE_COUNT);

  const largestAtRiskDealValue = topDealsAtRisk.length
    ? Math.max(...topDealsAtRisk.map(d => d.value ?? 0))
    : 0;

  const atRiskTotalDealValue = dealsAtRisk
    .map(d => d.value)
    .reduce((acc, val) => acc + val, 0);

  // TODO: Top risk PROPORTIONAL revenue exposure
  // at-risk deal value * risk %

  let topRisk = getTopRiskInCategory(dealsAtRisk, "pipelineRisk");

  if (topRisk !== null) {
    topRisk.pct = Math.round(100 * topRisk.value / (totalOpenDealValue ?? 1));
    topRisk.label = mapRiskName(topRisk.risk as any);
  }

  return {
    deals: dealsAtRisk,
    percentageValueAtRisk: totalOpenDealValue === 0
      ? 0
      : Math.round(atRiskTotalDealValue * 100 / totalOpenDealValue),
    topRisk,
    riskExposureSummaryByStage,
    riskExposureDeals,
    lowRiskDeals,
    lowRiskDealsTotalValue,
    lowRiskDealsTotalValuePctOfAll,
    lowRiskDealsRiskAdjustedTotalValue,
    mediumRiskDeals,
    mediumRiskDealsTotalValue,
    mediumRiskDealsTotalValuePctOfAll,
    mediumRiskDealsRiskAdjustedTotalValue,
    highRiskDeals,
    highRiskDealsTotalValue,
    highRiskDealsTotalValuePctOfAll,
    highRiskDealsRiskAdjustedTotalValue,
    riskBreakdownChartData,
    bubbleChartData,
    largestAtRiskDeal,
    largestAtRiskDealValue,
    concentratedDeals: concentratedDeals.slice(0, DEALS_TABLE_COUNT),
    pipelineConcentrationPct: pipelineConcentrationThresholdMet,
    pipelineConcentrationDealQty: pipelineConcentrationDealQuantity,
    pipelineConcentrationSummary,
    pipelineConcentrationShortSummary,
  };
}

export function evaluateOpenDealsForForecast(
  openDeals: any[],
  companyId: number | null | undefined,
  pipelines: any[],
  evaluationTime: Date = now,
) {
  const dealsReadyForRiskAssessment = openDeals.map(d => {
    const dealValueAsPctOfTotalValue = 10;
    const stageProbability = getRevelateStageProbabilityForDeal(pipelines, d);

    return {
      ...d,
      percentage_of_total_value: dealValueAsPctOfTotalValue,
      deal_scale: dealValueAsPctOfTotalValue,
      stage_scale: stageProbability,
    };
  });

  return evaluateRiskIndicators(
    dealsReadyForRiskAssessment,
    companyId,
    pipelines,
    undefined,
    evaluationTime,
  )
    .map(d => {
      const riskScore = getTotalDealRiskScoreInCategory(d, "forecast");
      const stageAdjustedValue = Math.round(
        Number(d.value ?? 0) * Number(d.stage_probability_adjusted ?? 0),
      );

      return {
        ...d,
        risk_score: riskScore,
        weighted_risk_score: getWeightedDealRiskScoreInCategory(d, "forecast"),
        forecast_contribution: stageAdjustedValue - Math.round(
          stageAdjustedValue * riskScore / 100,
        ),
      };
    })
    .toSorted((a, b) => b.weighted_risk_score - a.weighted_risk_score);
}

type PointInTimeForecastDeal = {
  contribution: number;
  expectedCloseDate: Date | null;
  existed: boolean;
  isOpen: boolean;
  isWon: boolean;
  isClosed: boolean;
  stageId: string | null;
  stageLabel: string | null;
  stageProbability: number | null;
  riskScore: number | null;
  riskIndicatorScores: Partial<Record<RiskIndicator, number>> | null;
  riskFallback: boolean;
};

function getForecastRiskIndicatorScores(deal: TODO | undefined) {
  if (!deal) return null;
  const scores: Partial<Record<RiskIndicator, number>> = {};
  for (const indicator of categoryIndicators.forecast) {
    const score = Number(deal.risks?.[indicator]?.risk);
    if (Number.isFinite(score)) scores[indicator] = score;
  }
  return scores;
}

function getPointInTimeHistoryRecords(
  deal: any,
  property: string,
  asOf: Date,
) {
  const history = deal.provider_data?.propertiesWithHistory?.[property];
  if (!Array.isArray(history)) return [];

  return history
    .filter(record => {
      if (!record?.timestamp) return false;
      const timestamp = new Date(record.timestamp);
      return !Number.isNaN(timestamp.getTime()) && !isAfter(timestamp, asOf);
    })
    .toSorted((a, b) => (
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    ));
}

function getPointInTimeProperty(
  deal: any,
  property: string,
  asOf: Date,
) {
  const records = getPointInTimeHistoryRecords(deal, property, asOf);
  return records.length
    ? { available: true, value: records[0]?.value }
    : { available: false, value: undefined };
}

function getHistoricalNormalizedDealValue(deal: any, asOf: Date) {
  const historicalAmount = getPointInTimeProperty(deal, "amount", asOf);
  if (!historicalAmount.available) return Number(deal.value ?? 0);

  const amount = Number(historicalAmount.value);
  if (!Number.isFinite(amount)) return Number(deal.value ?? 0);

  const currentRawValue = Number(deal.valueInDealCurrency ?? deal.value ?? 0);
  const currentNormalizedValue = Number(deal.value ?? 0);
  const conversionFactor = currentRawValue !== 0
    ? currentNormalizedValue / currentRawValue
    : 1;
  return amount * conversionFactor;
}

function getPointInTimeTimestampProperty(
  deal: any,
  property: string,
  asOf: Date,
) {
  const historical = getPointInTimeProperty(deal, property, asOf);
  if (historical.available) return historical;

  const currentValue = deal.provider_data?.properties?.[property];
  if (!currentValue) return { available: true, value: currentValue };

  const timestamp = new Date(currentValue);
  if (!Number.isNaN(timestamp.getTime()) && !isAfter(timestamp, asOf)) {
    return { available: true, value: currentValue };
  }

  return { available: false, value: undefined };
}

function buildPointInTimeForecastDeal(
  deal: any,
  companyId: number | null | undefined,
  pipelines: any[],
  dealstageAvgTimes: any[] | undefined,
  asOf: Date,
): PointInTimeForecastDeal | null {
  const createdDate = getDealCreatedDate(deal);
  if (!createdDate || isAfter(createdDate, asOf)) {
    return {
      contribution: 0,
      expectedCloseDate: null,
      existed: false,
      isOpen: false,
      isWon: false,
      isClosed: false,
      stageId: null,
      stageLabel: null,
      stageProbability: null,
      riskScore: null,
      riskIndicatorScores: null,
      riskFallback: false,
    };
  }

  const historicalStage = getPointInTimeProperty(deal, "dealstage", asOf);
  const historicalCloseDate = getPointInTimeProperty(deal, "closedate", asOf);
  if (!historicalStage.available || !historicalCloseDate.available) return null;

  const historicalPipeline = getPointInTimeProperty(deal, "pipeline", asOf);
  const pipelineId = historicalPipeline.available
    ? String(historicalPipeline.value)
    : deal.pipeline;
  const stageId = String(historicalStage.value);
  const pipeline = pipelines.find(item => item.id === pipelineId);
  if (!pipeline?.stages?.some((stage: any) => stage.id === stageId)) return null;

  const stageProbability = getStageProbability(
    pipelines,
    pipelineId,
    stageId,
    true,
  );
  const isOpen = stageProbability > 0 && stageProbability < 1;
  const isWon = stageProbability === 1;
  const stageLabel = getStageLabel(pipelines, pipelineId, stageId);
  if (!isOpen && !isWon) {
    return {
      contribution: 0,
      expectedCloseDate: null,
      existed: true,
      isOpen: false,
      isWon: false,
      isClosed: true,
      stageId,
      stageLabel,
      stageProbability,
      riskScore: null,
      riskIndicatorScores: null,
      riskFallback: false,
    };
  }

  const prunedHistories = Object.fromEntries(
    Object.keys(deal.provider_data?.propertiesWithHistory ?? {})
      .map(property => [
        property,
        getPointInTimeHistoryRecords(deal, property, asOf),
      ])
      .filter(([, records]) => (records as any[]).length > 0),
  );
  const historicalProperties: Record<string, any> = {
    createdate: deal.provider_data?.properties?.createdate,
    hs_createdate: deal.provider_data?.properties?.hs_createdate,
    closedate: historicalCloseDate.value,
    dealstage: stageId,
    pipeline: pipelineId,
  };
  let riskInputsAvailable = true;

  for (const property of [
    "notes_last_updated",
    "hs_lastmodifieddate",
    "hs_latest_sales_email_reply_date",
  ]) {
    const historical = getPointInTimeTimestampProperty(deal, property, asOf);
    if (historical.available) {
      historicalProperties[property] = historical.value;
    } else {
      riskInputsAvailable = false;
    }
  }

  const historicalValue = getHistoricalNormalizedDealValue(deal, asOf);
  const snapshotDeal = {
    ...structuredClone(deal),
    pipeline: pipelineId,
    closed_at: historicalCloseDate.value,
    is_closed: isWon,
    is_won: isWon,
    is_lost: false,
    value: historicalValue,
    risks: {},
    stage_probability: stageProbability,
    stage_probability_adjusted: stageProbability,
    provider_data: {
      ...structuredClone(deal.provider_data),
      properties: historicalProperties,
      propertiesWithHistory: prunedHistories,
    },
  };

  if (isWon) {
    return {
      contribution: Math.round(historicalValue),
      expectedCloseDate: historicalCloseDate.value
        ? new Date(historicalCloseDate.value)
        : null,
      existed: true,
      isOpen: false,
      isWon: true,
      isClosed: true,
      stageId,
      stageLabel,
      stageProbability,
      riskScore: null,
      riskIndicatorScores: null,
      riskFallback: false,
    };
  }

  const expectedCloseDate = getRevelateExpectedCloseDate(
    snapshotDeal,
    pipelines,
    dealstageAvgTimes,
    asOf,
  );
  const stageAdjustedContribution = Math.round(
    historicalValue * stageProbability,
  );

  if (!riskInputsAvailable) {
    return {
      contribution: stageAdjustedContribution,
      expectedCloseDate,
      existed: true,
      isOpen: true,
      isWon: false,
      isClosed: false,
      stageId,
      stageLabel,
      stageProbability,
      riskScore: null,
      riskIndicatorScores: null,
      riskFallback: true,
    };
  }

  const evaluatedDeal = evaluateOpenDealsForForecast(
    [snapshotDeal],
    companyId,
    pipelines,
    asOf,
  )[0];

  return {
    contribution: Number(evaluatedDeal?.forecast_contribution ?? stageAdjustedContribution),
    expectedCloseDate,
    existed: true,
    isOpen: true,
    isWon: false,
    isClosed: false,
    stageId,
    stageLabel,
    stageProbability,
    riskScore: Number(evaluatedDeal?.risk_score ?? 0),
    riskIndicatorScores: getForecastRiskIndicatorScores(evaluatedDeal),
    riskFallback: evaluatedDeal === undefined,
  };
}

function isDateInPeriod(
  value: Date | string | null | undefined,
  period: ManagementReviewPeriodRange,
) {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return isWithinInterval(date, { start: period.from, end: period.to });
}

function getStateOfSalesV2ReopenedDate(
  deal: TODO,
  pipelines: TODO[],
  snapshot: PointInTimeForecastDeal,
  asOf: Date,
) {
  const stageHistory = deal.provider_data?.propertiesWithHistory?.dealstage;
  if (!Array.isArray(stageHistory) || snapshot.stageProbability === null) return null;

  let previousProbability = snapshot.stageProbability;
  const records = stageHistory
    .filter(record => {
      const timestamp = new Date(record?.timestamp);
      return !Number.isNaN(timestamp.getTime())
        && isAfter(timestamp, asOf)
        && !isAfter(timestamp, now);
    })
    .toSorted((a, b) => (
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    ));

  for (const record of records) {
    const timestamp = new Date(record.timestamp);
    const historicalPipeline = getPointInTimeProperty(deal, "pipeline", timestamp);
    const pipelineId = historicalPipeline.available
      ? String(historicalPipeline.value)
      : deal.pipeline;
    const probability = getStageProbability(
      pipelines,
      pipelineId,
      String(record.value),
      true,
    );
    const reopened = (previousProbability === 0 || previousProbability === 1)
      && probability > 0
      && probability < 1;
    if (reopened) return timestamp;
    previousProbability = probability;
  }

  return null;
}

function getStateOfSalesV2OutlookChangeSummary(
  snapshot: PointInTimeForecastDeal,
  currentDeal: TODO,
) {
  const changes: string[] = [];
  const currentStage = currentDeal.stage_label ?? null;
  const currentProbability = Number(currentDeal.stage_probability_adjusted);
  const currentRiskScore = Number(currentDeal.risk_score);

  if (snapshot.stageLabel && currentStage && snapshot.stageLabel !== currentStage) {
    changes.push(`${snapshot.stageLabel} → ${currentStage}`);
  }
  if (
    snapshot.stageProbability !== null
    && Number.isFinite(currentProbability)
    && Math.abs(snapshot.stageProbability - currentProbability) >= 0.001
  ) {
    changes.push(
      `Probability ${Math.round(snapshot.stageProbability * 100)}% → ${Math.round(currentProbability * 100)}%`,
    );
  }
  if (
    snapshot.riskScore !== null
    && Number.isFinite(currentRiskScore)
    && Math.abs(snapshot.riskScore - currentRiskScore) >= 0.5
  ) {
    changes.push(`Risk ${formatRiskPointChange(snapshot.riskScore, currentRiskScore)}`);
  }

  return changes.join(" · ") || "Forecast outlook changed";
}

const stateOfSalesV2HistoricallySupportedForecastIndicators: RiskIndicator[] = [
  "stageProgression",
  "closeDateInPast",
  "closeDateSlippage",
  "lastModifiedDate",
  "emailResponseTime",
  "stageRegression",
];

function getStateOfSalesV2ChangedForecastIndicators(
  snapshot: PointInTimeForecastDeal | null,
  currentDeal: TODO | undefined,
) {
  if (!snapshot?.riskIndicatorScores || !currentDeal) return [];

  return stateOfSalesV2HistoricallySupportedForecastIndicators.flatMap(indicator => {
    const previousRisk = Number(snapshot.riskIndicatorScores?.[indicator]);
    const currentRisk = Number(currentDeal.risks?.[indicator]?.risk);
    if (
      !Number.isFinite(previousRisk)
      || !Number.isFinite(currentRisk)
      || Math.abs(previousRisk - currentRisk) < 0.5
    ) return [];

    return [{
      indicator,
      label: mapRiskName(indicator),
      previousRisk,
      currentRisk,
    }];
  });
}

function buildStateOfSalesV2ForecastMovement(
  currentDeals: TODO[],
  companyId: number | null | undefined,
  pipelines: TODO[],
  dealstageAvgTimes: TODO[] | undefined,
  period: ManagementReviewPeriodRange,
  asOf: Date,
): StateOfSalesV2ForecastMovement {
  const uniqueDeals = Array.from(
    new Map(currentDeals.map(deal => [deal.id, deal])).values(),
  );
  const currentOpenDeals = uniqueDeals.filter(
    deal => !deal.is_closed && !deal.is_won && !deal.is_lost,
  );
  const currentEvaluatedDeals = evaluateOpenDealsForForecast(
    currentOpenDeals.map(deal => structuredClone(deal)),
    companyId,
    pipelines,
  );
  const currentDealsById = new Map(
    currentEvaluatedDeals.map(deal => [deal.id, deal]),
  );

  let snapshotExpectedFromOpenDeals = 0;
  let snapshotWon = 0;
  let newDeals = 0;
  let closedWon = 0;
  let closedLost = 0;
  let movedIn = 0;
  let movedOut = 0;
  let improvedOutlook = 0;
  let weakenedOutlook = 0;
  let riskFallbackDealCount = 0;
  let supportedSnapshotDealCount = 0;
  const dealsByCategory: StateOfSalesV2ForecastMovement["dealsByCategory"] = {
    new_deals: [],
    closed_won: [],
    closed_lost: [],
    moved_in: [],
    moved_out: [],
    improved_outlook: [],
    weakened_outlook: [],
    unclassified: [],
  };

  const addDeal = (
    key: StateOfSalesV2ForecastMovementCategoryKey,
    deal: TODO,
    forecastImpact: number,
    previousForecastContribution: number | null,
    currentForecastContribution: number | null,
    snapshot: PointInTimeForecastDeal | null,
    currentDeal: TODO | undefined,
    missingHistoricalInput: string | null = null,
  ) => {
    const owner = getDealOwner(deal);
    const reopenedAt = snapshot?.isClosed && currentDeal
      ? getStateOfSalesV2ReopenedDate(deal, pipelines, snapshot, asOf)
      : null;
    const createdAt = getDealCreatedDate(deal);
    const actualCloseDate = deal.closed_at ? new Date(deal.closed_at) : null;
    const changeObservedAt = key === "new_deals"
      ? reopenedAt ?? createdAt
      : key === "closed_won" || key === "closed_lost"
        ? actualCloseDate
        : null;
    const currentRisk = Number(currentDeal?.risk_score);
    dealsByCategory[key].push({
      id: String(deal.id),
      name: deal.name ?? "Unnamed deal",
      deal,
      forecastImpact: Math.round(forecastImpact),
      previousForecastContribution: previousForecastContribution === null
        ? null
        : Math.round(previousForecastContribution),
      currentForecastContribution: currentForecastContribution === null
        ? null
        : Math.round(currentForecastContribution),
      previousRisk: snapshot?.riskScore ?? null,
      currentRisk: Number.isFinite(currentRisk) ? currentRisk : null,
      changedIndicators: getStateOfSalesV2ChangedForecastIndicators(snapshot, currentDeal),
      indicatorHistoryAvailable: Boolean(snapshot?.riskIndicatorScores && currentDeal),
      ownerName: owner ? getFullName(owner) : "Not specified",
      createdOrReopenedAt: (reopenedAt ?? createdAt)?.toISOString() ?? null,
      currentStage: currentDeal?.stage_label ?? deal.stage_label ?? null,
      previousStage: snapshot?.stageLabel ?? null,
      previousExpectedClose: snapshot?.expectedCloseDate?.toISOString() ?? null,
      currentExpectedClose: currentDeal?.adjustedCloseDate?.toISOString?.()
        ?? (currentDeal?.adjustedCloseDate ? new Date(currentDeal.adjustedCloseDate).toISOString() : null),
      actualCloseDate: actualCloseDate?.toISOString() ?? null,
      includedInStartForecast: Boolean(
        snapshot
        && (snapshot.isOpen || snapshot.isWon)
        && isDateInPeriod(snapshot.expectedCloseDate, period)
      ),
      changeSummary: snapshot && currentDeal
        ? getStateOfSalesV2OutlookChangeSummary(snapshot, currentDeal)
        : null,
      changeObservedAt: changeObservedAt?.toISOString() ?? null,
      comparisonPeriodStart: asOf.toISOString(),
      comparisonPeriodEnd: now.toISOString(),
      missingHistoricalInput,
    });
  };

  const isInComparisonWindow = (value: Date | string | null | undefined) => {
    if (!value) return false;
    const date = new Date(value);
    return !Number.isNaN(date.getTime())
      && !isBefore(date, asOf)
      && !isAfter(date, now);
  };

  for (const deal of uniqueDeals) {
    const currentDeal = currentDealsById.get(deal.id);
    const currentOpenInPeriod = Boolean(
      currentDeal && isDateInPeriod(currentDeal.adjustedCloseDate, period),
    );
    const currentOpenContribution = currentOpenInPeriod
      ? Number(currentDeal?.forecast_contribution ?? 0)
      : 0;
    const currentWonInPeriod = Boolean(
      deal.is_won && isDateInPeriod(deal.closed_at, period),
    );
    const currentContribution = currentWonInPeriod
      ? Number(deal.value ?? 0)
      : currentOpenContribution;
    const snapshot = buildPointInTimeForecastDeal(
      deal,
      companyId,
      pipelines,
      dealstageAvgTimes,
      asOf,
    );
    if (snapshot === null) {
      addDeal(
        "unclassified",
        deal,
        currentContribution,
        null,
        currentContribution,
        null,
        currentDeal,
        "Required stage or close-date history is unavailable",
      );
      continue;
    }

    const snapshotInPeriod = snapshot.isOpen
      ? isDateInPeriod(snapshot.expectedCloseDate, period)
      : snapshot.isWon && isDateInPeriod(snapshot.expectedCloseDate, period);
    if (snapshotInPeriod) {
      supportedSnapshotDealCount += 1;
      if (snapshot.isOpen) snapshotExpectedFromOpenDeals += snapshot.contribution;
      if (snapshot.isWon) snapshotWon += snapshot.contribution;
      if (snapshot.riskFallback) riskFallbackDealCount += 1;
    }

    const snapshotContribution = snapshotInPeriod ? snapshot.contribution : 0;
    const contributionChange = currentContribution - snapshotContribution;

    if (deal.is_won && isInComparisonWindow(deal.closed_at)) {
      closedWon += contributionChange;
      addDeal(
        "closed_won",
        deal,
        contributionChange,
        snapshotContribution,
        currentContribution,
        snapshot,
        currentDeal,
      );
      continue;
    }
    if (deal.is_lost && isInComparisonWindow(deal.closed_at)) {
      closedLost -= snapshotContribution;
      addDeal(
        "closed_lost",
        deal,
        -snapshotContribution,
        snapshotContribution,
        0,
        snapshot,
        currentDeal,
      );
      continue;
    }

    if (deal.is_won || deal.is_lost) {
      continue;
    }

    if (!currentDeal) {
      if (contributionChange !== 0 || deal.is_closed) {
        addDeal(
          "unclassified",
          deal,
          contributionChange,
          snapshotContribution,
          currentContribution,
          snapshot,
          currentDeal,
          "Current open or closed state could not be reconstructed",
        );
      }
      continue;
    }

    if (!snapshot.existed || snapshot.isClosed) {
      newDeals += currentContribution;
      addDeal(
        "new_deals",
        deal,
        currentContribution,
        0,
        currentContribution,
        snapshot,
        currentDeal,
      );
      continue;
    }

    if (snapshot.isOpen && !snapshotInPeriod && currentOpenInPeriod) {
      movedIn += currentContribution;
      addDeal(
        "moved_in",
        deal,
        currentContribution,
        0,
        currentContribution,
        snapshot,
        currentDeal,
      );
      continue;
    }
    if (snapshot.isOpen && snapshotInPeriod && !currentOpenInPeriod) {
      movedOut -= snapshot.contribution;
      addDeal(
        "moved_out",
        deal,
        -snapshot.contribution,
        snapshot.contribution,
        0,
        snapshot,
        currentDeal,
      );
      continue;
    }
    if (!snapshotInPeriod && !currentOpenInPeriod) continue;

    if (contributionChange > 0) {
      improvedOutlook += contributionChange;
      addDeal(
        "improved_outlook",
        deal,
        contributionChange,
        snapshotContribution,
        currentContribution,
        snapshot,
        currentDeal,
      );
    }
    if (contributionChange < 0) {
      weakenedOutlook += contributionChange;
      addDeal(
        "weakened_outlook",
        deal,
        contributionChange,
        snapshotContribution,
        currentContribution,
        snapshot,
        currentDeal,
      );
    }
  }

  for (const deals of Object.values(dealsByCategory)) {
    deals.sort((a, b) => Math.abs(b.forecastImpact) - Math.abs(a.forecastImpact));
  }

  const expectedFromOpenDeals = Math.round(snapshotExpectedFromOpenDeals);
  const won = Math.round(snapshotWon);

  return {
    asOf: asOf.toISOString(),
    available: supportedSnapshotDealCount > 0 || currentOpenDeals.length > 0,
    startForecast: expectedFromOpenDeals + won,
    expectedFromOpenDeals,
    won,
    values: {
      newDeals: Math.round(newDeals),
      closedWon: Math.round(closedWon),
      closedLost: Math.round(closedLost),
      movedIn: Math.round(movedIn),
      movedOut: Math.round(movedOut),
      improvedOutlook: Math.round(improvedOutlook),
      weakenedOutlook: Math.round(weakenedOutlook),
    },
    dealsByCategory,
    riskFallbackDealCount,
  };
}

export function evaluateRiskForecast(
  hubspotDeals: any[],
  companyId: number | null | undefined,
  pipelines: any[],
  forecastTimePeriod: ForecastTimePeriod,
  selectedPipeline: string,
  selectedOwner: string,
  companyTarget: number,
  isAccumulated: boolean,
  companyCurrency: any | null,
  stateOfSalesV2Options?: {
    includeL2Details?: boolean;
    includeOpenDeals?: boolean;
    movementDeals?: any[];
    dealstageAvgTimes?: any[];
  },
) {
  const periodRange = getForecastTimePeriodRange(forecastTimePeriod);
  const startOfTimePeriod = periodRange.start;

  const startOfChartTimePeriod = forecastTimePeriod === "this_year" || forecastTimePeriod === "next_6_months"
    ? startOfLastMonth
    : startOfTimePeriod;

  const endOfTimePeriod = periodRange.end;

  const filteredDeals = hubspotDeals.filter(deal => {
    if (selectedPipeline !== "all" && deal.pipeline !== selectedPipeline) return false;
    if (selectedOwner !== "all" && getDealOwner(deal) === null) return false;
    if (selectedOwner !== "all" && getDealOwner(deal)?.id !== selectedOwner) return false;
    return true;
  })

  const filteredDealsInTimePeriod = filteredDeals;

  const filteredDealsInSelectedTimePeriod = filteredDeals
    .filter(d => d.is_closed
      ? isWithinInterval(d.closed_at, {
        start: startOfTimePeriod,
        end: endOfTimePeriod,
      })
      : isWithinInterval(d.adjustedCloseDate, {
        start: startOfTimePeriod,
        end: endOfTimePeriod,
      })

    );

  const openDealsInTimePeriod = filteredDealsInTimePeriod
    .filter(d => !d.is_closed && !d.is_won && !d.is_lost);

  const openDealsInSelectedTimePeriod = filteredDealsInSelectedTimePeriod
    .filter(d => !d.is_closed && !d.is_won && !d.is_lost);

  // const totalOpenDealValueInTimePeriod = openDealsInTimePeriod
  //   .map(d => d.value)
  //   .reduce((acc, val) => acc + val, 0);

  const totalOpenDealValueInSelectedTimePeriod = openDealsInSelectedTimePeriod
    .map(d => d.value)
    .reduce((acc, val) => acc + val, 0);

  const wonDealsInTimePeriod = filteredDealsInTimePeriod
    .filter(d => d.is_won);

  const wonDealsInSelectedTimePeriod = filteredDealsInSelectedTimePeriod
    .filter(d => d.is_won);

  // const totalWonDealValueInTimePeriod = wonDealsInTimePeriod
  //   .map(d => d.value)
  //   .reduce((acc, val) => acc + val, 0);

  const totalWonDealValueInSelectedTimePeriod = wonDealsInSelectedTimePeriod
    .map(d => d.value)
    .reduce((acc, val) => acc + val, 0);

  const dealsInTimePeriodWithRiskScores = evaluateOpenDealsForForecast(
    openDealsInTimePeriod,
    companyId,
    pipelines,
  );

  const forecastChartData = formatDealsForForecastTrendChart(
    dealsInTimePeriodWithRiskScores,
    wonDealsInTimePeriod,
    companyTarget,
    startOfChartTimePeriod,
    endOfTimePeriod,
    isAccumulated
  );

  // State of Sales v2 renders one chart-only context month before the selected
  // period. Calculate the context and selected-period series separately so
  // accumulated values reset at the selected-period boundary. Keep the legacy
  // forecastChartData output unchanged for its existing consumers.
  const startOfForecastChartContext = startOfMonth(subMonths(startOfTimePeriod, 1));
  const endOfForecastChartContext = endOfMonth(startOfForecastChartContext);
  const forecastChartDataWithContext = [
    ...formatDealsForForecastTrendChart(
      dealsInTimePeriodWithRiskScores,
      wonDealsInTimePeriod,
      companyTarget,
      startOfForecastChartContext,
      endOfForecastChartContext,
      false,
    ),
    ...formatDealsForForecastTrendChart(
      dealsInTimePeriodWithRiskScores,
      wonDealsInTimePeriod,
      companyTarget,
      startOfTimePeriod,
      endOfTimePeriod,
      isAccumulated,
    ),
  ];

  // Get last 30d forecast diff from chart
  const currentMonthChartKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const lastMonthChartKey = `${now.getFullYear()}-${String(now.getMonth()).padStart(2, '0')}`;

  const currentMonthForecastFromChart = forecastChartData?.find(row => row.month === currentMonthChartKey)?.total_minus_risk ?? 0;
  const lastMonthForecastFromChart = forecastChartData?.find(row => row.month === lastMonthChartKey)?.total_minus_risk ?? 0;

  const hideForecastInsight = currentMonthForecastFromChart === 0 && lastMonthForecastFromChart === 0;

  const dealsInSelectedTimePriod = dealsInTimePeriodWithRiskScores
    .filter(d => isWithinInterval(d.adjustedCloseDate, {
      start: startOfTimePeriod,
      end: endOfTimePeriod,
    }))

  const top3Risks = getTop3RisksInCategory(dealsInSelectedTimePriod, "forecast");

  // const earlyStageTotalValueInTimePeriod = dealsInSelectedTimePriod
  //   .filter(d => d.stage_probability_adjuted <= 0.25)
  //   .map(d => d.value)
  //   .reduce((acc, val) => acc + val, 0);
  //
  // const lateStageTotalValueInTimePeriod = dealsInSelectedTimePriod
  //   .filter(d => d.stage_probability >= 0.75)
  //   .map(d => d.value)
  //   .reduce((acc, val) => acc + val, 0);

  // const highestRiskContributionStageInTimePeriod = earlyStageTotalValueInTimePeriod > lateStageTotalValueInTimePeriod
  //   ? "early-stage"
  //   : "late-stage";

  // const riskScoresInTimePeriod = dealsInTimePeriodWithRiskScores
  //   .map(d => d.risk_score)

  // const totalRiskScoreInTimePeriod = riskScoresInTimePeriod.reduce((acc, val) => acc + val, 0);
  // const avgRiskScoreInTimePeriod = totalRiskScoreInTimePeriod / riskScoresInTimePeriod.length;

  // const reliabilityInTimePeriod = avgRiskScoreInTimePeriod >= 25 ? "low" : "high";


  const dealsAtRiskInSelectedTimePeriod = dealsInSelectedTimePriod
    .filter(d => d.risk_score > 0);

  const revenueAtRiskInSelectedTimePeriod = dealsAtRiskInSelectedTimePeriod
    .filter(d => d.risk_score >= 50)
    .map(d => d.value)
    .reduce((acc, val) => acc + val, 0);

  const atRiskWeightedDealValue = dealsAtRiskInSelectedTimePeriod
    .reduce((acc, deal) => acc + (deal.value * deal.risk_score / 100), 0);

  // 6 months + prev month
  const numMonthsForNext6MonthsPeriod = 7;

  const revenueTarget = forecastTimePeriod === "this_month" || forecastTimePeriod === "next_month"
    ? companyTarget
    : forecastTimePeriod === "next_6_months"
      ? companyTarget * numMonthsForNext6MonthsPeriod
      : forecastTimePeriod === "this_year"
        ? companyTarget * 12
        : companyTarget * 3; // quarter

  const totalDealValue = Math.round(totalOpenDealValueInSelectedTimePeriod + totalWonDealValueInSelectedTimePeriod);
  const forecast = Math.round(totalDealValue - atRiskWeightedDealValue);
  const revenueGap = Math.round(forecast - revenueTarget);

  // Snapshot
  // Idea is to forecast THE SAME PERIOD but only using
  // information available 30d ago
  const snapshotTime = subDays(now, 30);

  // Start with same deals in selected time period
  // Then remove any deals which were created after snapshot time
  // Then simulate the deals as they would have been at snapshot time
  // and calculate the risks and forecast
  const filteredDealsInSnapshotPeriod = [...filteredDealsInSelectedTimePeriod]
    .filter(d => simulateDealAtSnapshotTime(d, snapshotTime));

  const dealIdsWhichExistedAtSnapshotTime = filteredDealsInSnapshotPeriod
    .filter(d => !isAfter(
      d.provider_data?.properties?.createdate ??
      d.provider_data?.properties?.hs_createdate ??
      "",
      snapshotTime))
    .map(d => d.id)

  // Two test: Only one deal of 140_000
  // const newDealsAfterSnapshotTime = filteredDealsInSelectedTimePeriod
  //   .filter(deal => !dealIdsWhichExistedAtSnapshotTime.includes(deal.id))

  // NOTE: Deals are alredy simulated to be at snapshot time
  // so this can very likely be simplified
  const openDealsAtSnapshotTime = filteredDealsInSnapshotPeriod
    .filter(d => {
      const dealstageAtSnapshotTime = getHistoricalPropertyAtSnapshotDate(
        d,
        "dealstage",
        snapshotTime,
      );
      const probabilityAtSnapshotTime = getStageProbability(
        pipelines,
        d.pipeline,
        dealstageAtSnapshotTime,
      );
      const isOpen = probabilityAtSnapshotTime > 0 && probabilityAtSnapshotTime < 1;
      return isOpen;
    })
    .filter(d => dealIdsWhichExistedAtSnapshotTime.includes(d.id))

  const totalOpenDealValueAtSnapshotTime = openDealsAtSnapshotTime
    .map(d => d.value)
    .reduce((acc, val) => acc + val, 0);

  const wonDealsAtSnapshotTime = filteredDealsInSnapshotPeriod
    .filter(d => {
      const dealstageAtSnapshotTime = getHistoricalPropertyAtSnapshotDate(
        d,
        "dealstage",
        snapshotTime,
      );

      if (!dealstageAtSnapshotTime) return false;

      const probabilityAtSnapshotTime = getStageProbability(
        pipelines,
        d.pipeline,
        dealstageAtSnapshotTime
      );

      return probabilityAtSnapshotTime === 1;
    })
    .filter(d => dealIdsWhichExistedAtSnapshotTime.includes(d.id));

  const totalWonDealValueAtSnapshotTime = wonDealsAtSnapshotTime
    .map(d => d.value)
    .reduce((acc, val) => acc + val, 0);


  // NOTE: Deals at snapshot time are already simulated
  // so this can likely be simplified and unified with that logic
  const openDealsAtSnapshotTimeReadyForRiskAssessment = openDealsAtSnapshotTime.map(d => {
    // NOTE: Setting to full 10 to avoid taking this factor into account
    const dealValueAsPctOfTotalValue = 10;

    const dealstageAtSnapshotTime = getHistoricalPropertyAtSnapshotDate(
      d,
      "dealstage",
      snapshotTime,
    );
    const probabilityAtSnapshotTime = getStageProbability(
      pipelines,
      d.pipeline,
      dealstageAtSnapshotTime,
      true, // fromHistoricalData
    );

    const closeDate = d.closed_at ? new Date(d.closed_at) : undefined;
    const dealstageAtCloseTime = getHistoricalPropertyAtSnapshotDate(
      d,
      "dealstage",
      closeDate,
    );
    const probabilityAtCloseTime = getStageProbability(
      pipelines,
      d.pipeline,
      dealstageAtCloseTime ?? null,
      true, // fromHistoricalData
    );

    return {
      ...d,
      percentage_of_total_value: dealValueAsPctOfTotalValue,
      deal_scale: dealValueAsPctOfTotalValue,
      stage_scale_at_snapshot_time: probabilityAtSnapshotTime,
      stage_scale_at_close_time: probabilityAtCloseTime,
    };
  })

  const openDealsAtSnapshotTimeWithRiskIndicatorsEvaluated = evaluateRiskIndicators(
    openDealsAtSnapshotTimeReadyForRiskAssessment,
    companyId,
    pipelines,
    undefined,
    snapshotTime,
  );

  const openDealsAtSnapshotTimeWithRiskScores = openDealsAtSnapshotTimeWithRiskIndicatorsEvaluated
    .map((d: any) => ({
      ...d,
      risk_score: getTotalDealRiskScoreInCategory(d, "forecast"),
      weighted_risk_score: getWeightedDealRiskScoreInCategory(d, "forecast"),
    }))
    .toSorted((a, b) => b.risk_score - a.risk_score);

  // State of Sales v2 needs a point-in-time comparison that uses the exact
  // value basis of the Forecast curve. Keep this separate from the legacy
  // waterfall values so existing Forecast outputs remain unchanged.
  const dealsWhichExistedAtSnapshotTime = filteredDealsInSelectedTimePeriod
    .filter(d => !isAfter(
      d.provider_data?.properties?.createdate
      ?? d.provider_data?.properties?.hs_createdate
      ?? "",
      snapshotTime,
    ));

  const forecastSnapshotDeals = dealsWhichExistedAtSnapshotTime.map((deal) => {
    const stageHistory = deal.provider_data?.propertiesWithHistory?.dealstage;
    const hasStageHistoryAtSnapshot = Array.isArray(stageHistory)
      && stageHistory.some((record) => record.timestamp
        && !isAfter(record.timestamp, snapshotTime));

    if (!hasStageHistoryAtSnapshot) return null;

    const simulatedDeal = simulateDealAtSnapshotTime(deal, snapshotTime);
    const historicalProperties = {
      ...(simulatedDeal.provider_data?.properties ?? {}),
    };

    // The established simulator stores reconstructed values on provider_data.
    // Copy those reviewed outputs into the properties shape consumed by the
    // risk evaluators without changing the simulator's existing consumers.
    for (const property of Object.keys(
      simulatedDeal.provider_data?.propertiesWithHistory ?? {},
    )) {
      const historicalValue = simulatedDeal.provider_data?.[property];
      if (historicalValue !== undefined) {
        historicalProperties[property] = historicalValue;
      }
    }

    const dealstageAtSnapshotTime = getHistoricalPropertyAtSnapshotDate(
      deal,
      "dealstage",
      snapshotTime,
    );
    const probabilityAtSnapshotTime = getStageProbability(
      pipelines,
      deal.pipeline,
      dealstageAtSnapshotTime,
      true,
    );

    return {
      ...simulatedDeal,
      provider_data: {
        ...simulatedDeal.provider_data,
        properties: historicalProperties,
      },
      percentage_of_total_value: 10,
      deal_scale: 10,
      stage_probability_adjusted: probabilityAtSnapshotTime,
      stage_scale: probabilityAtSnapshotTime,
      stage_scale_at_snapshot_time: probabilityAtSnapshotTime,
    };
  });

  const forecastSnapshotHasRequiredHistory = forecastSnapshotDeals.length > 0
    && forecastSnapshotDeals.every((deal) => deal !== null);
  const validForecastSnapshotDeals = forecastSnapshotDeals.filter(
    (deal): deal is NonNullable<typeof deal> => deal !== null,
  );
  const forecastSnapshotOpenDeals = validForecastSnapshotDeals.filter(
    deal => deal.stage_probability_adjusted > 0
      && deal.stage_probability_adjusted < 1,
  );
  const forecastSnapshotWonDeals = validForecastSnapshotDeals.filter(
    deal => deal.stage_probability_adjusted === 1,
  );
  const forecastSnapshotOpenDealsWithRisk = evaluateRiskIndicators(
    forecastSnapshotOpenDeals,
    companyId,
    pipelines,
    undefined,
    snapshotTime,
  ).map((deal) => ({
    ...deal,
    risk_score: getTotalDealRiskScoreInCategory(deal, "forecast"),
  }));
  const forecastSnapshotWon = forecastSnapshotWonDeals.reduce(
    (sum, deal) => sum + Number(deal.value ?? 0),
    0,
  );
  const forecastSnapshotExpectedFromOpenDeals = forecastSnapshotOpenDealsWithRisk.reduce(
    (sum, deal) => sum + (
      Number(deal.value ?? 0)
      * Number(deal.stage_probability_adjusted ?? 0)
      * (100 - Number(deal.risk_score ?? 0))
      / 100
    ),
    0,
  );
  const forecastSnapshotForecast = Math.round(
    forecastSnapshotWon + forecastSnapshotExpectedFromOpenDeals,
  );
  const forecastSnapshot = {
    asOf: snapshotTime.toISOString(),
    available: forecastSnapshotHasRequiredHistory,
    expectedFromOpenDeals: Math.round(forecastSnapshotExpectedFromOpenDeals),
    // Keep the earlier field during the local iteration while exposing the
    // canonical State of Sales v2 metric name.
    expectedToClose: forecastSnapshotForecast,
    forecast: forecastSnapshotForecast,
    won: Math.round(forecastSnapshotWon),
  };
  const stateOfSalesV2ForecastMovement = stateOfSalesV2Options?.movementDeals
    ? buildStateOfSalesV2ForecastMovement(
      [
        ...filteredDeals,
        ...stateOfSalesV2Options.movementDeals,
      ],
      companyId,
      pipelines,
      stateOfSalesV2Options.dealstageAvgTimes,
      { from: startOfTimePeriod, to: endOfTimePeriod },
      snapshotTime,
    )
    : null;
  const stateOfSalesV2ForecastSnapshot = stateOfSalesV2ForecastMovement?.available
    ? {
      asOf: stateOfSalesV2ForecastMovement.asOf,
      available: true,
      expectedFromOpenDeals: stateOfSalesV2ForecastMovement.expectedFromOpenDeals,
      expectedToClose: stateOfSalesV2ForecastMovement.startForecast,
      forecast: stateOfSalesV2ForecastMovement.startForecast,
      won: stateOfSalesV2ForecastMovement.won,
    }
    : forecastSnapshot;
  const includeStateOfSalesV2L2Details = Boolean(
    stateOfSalesV2Options?.includeL2Details,
  );
  const includeStateOfSalesV2OpenDeals = includeStateOfSalesV2L2Details
    || Boolean(stateOfSalesV2Options?.includeOpenDeals);
  const stateOfSalesV2LossSignalGroups = includeStateOfSalesV2L2Details
    ? getStateOfSalesV2LossSignalGroups(
      filteredDealsInSelectedTimePeriod,
      companyId,
      pipelines,
      snapshotTime,
    )
    : [];

  const openDealsAtRiskAtSnapshotTime = openDealsAtSnapshotTimeWithRiskScores
    .filter(d => d.risk_score > 0);

  const revenueAtRiskAtSnapshotTime = openDealsAtRiskAtSnapshotTime
    .filter(d => d.risk_score >= 50)
    .map(d => d.value)
    .reduce((acc, val) => acc + val, 0);

  const revenueAtRiskTrendPct = revenueAtRiskAtSnapshotTime === 0
    ? 0
    : 100 * (revenueAtRiskInSelectedTimePeriod - revenueAtRiskAtSnapshotTime) / revenueAtRiskAtSnapshotTime;

  const revenueAtRiskTrendIsGood = revenueAtRiskTrendPct <= 0;

  const atRiskWeightedDealValueAtSnapshotTime =
    openDealsAtSnapshotTimeWithRiskScores
      .reduce(
        (acc, deal) =>
          acc + (deal.value * deal.risk_score / 100),
        0
      );

  const totalDealValueAtSnapshotTime = Math.round(
    totalOpenDealValueAtSnapshotTime +
    totalWonDealValueAtSnapshotTime
  );

  const forecastAtSnapshotTime = Math.round(
    totalDealValueAtSnapshotTime -
    atRiskWeightedDealValueAtSnapshotTime
  );

  const forecastDiffTodayVs30dAgo = currentMonthForecastFromChart - lastMonthForecastFromChart;
  const forecastDiffText = forecastDiffTodayVs30dAgo > 0
    ? `Forecast revenue has increased with ${formatCurrencyValue(forecastDiffTodayVs30dAgo, companyCurrency, 1)} over the last 30 days.`
    : forecastDiffTodayVs30dAgo < 0
      ? `Forecast revenue has decreased with ${formatCurrencyValue(-forecastDiffTodayVs30dAgo, companyCurrency, 1)} over the last 30 days.`
      : `Forecast revenue has remained unchanged over the last 30 days.`;

  // const forecastInsight = `${forecastDiffText} Forecast is primarily supported by ${highestRiskContributionStageInTimePeriod} deals.`
  const forecastInsight = `${forecastDiffText}`

  // TODO: risk waterfall chart
  // Make into separate function

  const currentAggregateRiskGroups =
    getAggregateRiskValuesByIndicatorGroup(
      dealsInTimePeriodWithRiskScores
    );

  const snapshotAggregateRiskGroups =
    getAggregateRiskValuesByIndicatorGroup(
      openDealsAtSnapshotTimeWithRiskScores
    );

  const aggregateRiskGroupKeys = [
    ...new Set([
      ...Object.keys(currentAggregateRiskGroups),
      ...Object.keys(snapshotAggregateRiskGroups),
    ]),
  ]
    .filter(key => key !== "portfolioRisk");

  const aggregateRiskWaterfallChartDeltaBars = aggregateRiskGroupKeys.map(riskGroupKey => ({
    type: "risk",
    label: mapRiskGroupName(riskGroupKey as RiskIndicatorGroup),
    // Note: Risks interpreted as a reduction in forecast, therefore negative
    value:
      (-1 *
        Math.round(
          (currentAggregateRiskGroups[riskGroupKey] ?? 0) -
          (snapshotAggregateRiskGroups[riskGroupKey] ?? 0)
        )
      ),
  }));

  const forecastWaterfallChartDataWithoutCumulativeShifts = [
    {
      type: "start",
      // FIXME: More accurate name
      label: "30d before",
      value: forecastAtSnapshotTime,
    },
    ...aggregateRiskWaterfallChartDeltaBars,
    {
      type: "end",
      // FIXME: More accurate name
      label: "End of period",
      value: forecast,
    },
  ];

  const forecastWaterfallChartData = forecastWaterfallChartDataWithoutCumulativeShifts.map(
    (item, index, arr) => {
      // Calculate where the last bar "left off" so the next bar
      // can stack on top of that
      // But don't apply cumulative shift for the last item
      const cumulativeBefore =
        index === arr.length - 1
          ? 0
          : arr.slice(0, index).reduce((sum, i) => sum + i.value, 0);

      return {
        ...item,
        cumulativeBefore,
        // For tooltip and easier debugging
        // cumulativeAfter: cumulativeBefore + item.value,
      };
    }
  );

  return {
    deals: dealsAtRiskInSelectedTimePeriod,
    stateOfSalesV2OpenDeals: includeStateOfSalesV2OpenDeals ? dealsInSelectedTimePriod : [],
    stateOfSalesV2SelectedPeriodDeals: includeStateOfSalesV2L2Details
      ? filteredDealsInSelectedTimePeriod
      : [],
    stateOfSalesV2SelectedPeriodRange: {
      from: startOfTimePeriod,
      to: endOfTimePeriod,
    },
    stateOfSalesV2LossSignalGroups,
    revenueTarget,
    revenueGap,
    revenueAtRisk: Math.round(revenueAtRiskInSelectedTimePeriod),
    revenueAtRiskTrendPct,
    revenueAtRiskTrendIsGood,
    forecast,
    forecastChartData,
    forecastChartDataWithContext,
    forecastChartSelectedPeriodStart: format(startOfTimePeriod, "yyyy-MM"),
    forecastSnapshot: stateOfSalesV2ForecastSnapshot,
    stateOfSalesV2ForecastMovement,
    forecastWaterfallChartData,
    forecastInsight,
    hideForecastInsight,
    top3Risks,
  };
}

const msInDay = 86_400_000; // 24h * 60m * 60s * 1000
const msInAvgMonth = 30.4 * msInDay;

function getManagementReviewPeriod(periodRange?: ManagementReviewPeriodRange) {
  return {
    from: periodRange?.from ?? startOfThisQuarter,
    to: periodRange?.to ?? endOfThisQuarter,
  };
}

function getDealCreatedDate(deal: TODO) {
  const createdDate =
    deal.provider_data?.properties?.createdate ??
    deal.provider_data?.properties?.hs_createdate;

  return createdDate ? new Date(createdDate) : null;
}

function getDealExpectedCloseDate(deal: TODO) {
  const expectedCloseDate = deal.adjustedCloseDate ?? deal.closed_at;
  return expectedCloseDate ? new Date(expectedCloseDate) : null;
}

function isExpectedToCloseInPeriod(deal: TODO, periodRange: ManagementReviewPeriodRange) {
  const expectedCloseDate = getDealExpectedCloseDate(deal);
  if (!expectedCloseDate) return false;

  return isWithinInterval(expectedCloseDate, {
    start: periodRange.from,
    end: periodRange.to,
  });
}

function getPipelineMovementPeriod(periodRange: ManagementReviewPeriodRange): ManagementReviewPeriodRange | null {
  if (isAfter(periodRange.from, now)) return null;

  if (isWithinInterval(now, {
    start: periodRange.from,
    end: periodRange.to,
  })) {
    return {
      from: periodRange.from,
      to: now,
    };
  }

  return periodRange;
}

function isOpenAtSnapshotTime(deal: TODO, pipelines: TODO[], snapshotTime: Date) {
  const dealstageAtSnapshotTime = getHistoricalPropertyAtSnapshotDate(deal, "dealstage", snapshotTime);
  const probabilityAtSnapshotTime = getStageProbability(pipelines, deal.pipeline, dealstageAtSnapshotTime);
  return probabilityAtSnapshotTime > 0 && probabilityAtSnapshotTime < 1;
}

function getUrgencyBucket(index: number, total: number): ManagementReviewUrgencyRisk["urgency_bucket"] {
  const bucketSize = Math.ceil(total / 3);
  if (index < bucketSize) return "high";
  if (index < bucketSize * 2) return "medium";
  return "low";
}

export function getManagementReviewUrgencyRisks(dealsWithRiskScores: TODO[]): ManagementReviewUrgencyRisk[] {
  const urgencyWindowEnd = addDays(now, 60);

  const candidates = dealsWithRiskScores
    .filter(d => !d.is_closed)
    .filter(d => d.value > 0)
    .filter(d => {
      const expectedCloseDate = getDealExpectedCloseDate(d);
      if (!expectedCloseDate) return false;

      return isWithinInterval(expectedCloseDate, {
        start: now,
        end: urgencyWindowEnd,
      });
    })
    .map(d => ({
      ...d,
      urgency_score: d.risk_score * d.value,
    }))
    .toSorted((a, b) => b.urgency_score - a.urgency_score)
    .slice(0, 5);

  return candidates.map((deal, index) => ({
    ...deal,
    urgency_bucket: getUrgencyBucket(index, candidates.length),
  }));
}

function getManagementReviewPipelineMovement(
  dealsWithRiskScores: TODO[],
  pipelines: TODO[],
  periodRange: ManagementReviewPeriodRange,
): ManagementReviewPipelineMovementRow[] {
  const movementPeriod = getPipelineMovementPeriod(periodRange);
  if (!movementPeriod) return [];

  const dealsExpectedInPeriod = dealsWithRiskScores
    .filter(d => isExpectedToCloseInPeriod(d, movementPeriod));

  const startPipelineDeals = dealsExpectedInPeriod
    .filter(d => {
      const createdDate = getDealCreatedDate(d);
      if (!createdDate || isAfter(createdDate, movementPeriod.from)) return false;
      return isOpenAtSnapshotTime(d, pipelines, movementPeriod.from);
    });

  const newPipelineDeals = dealsExpectedInPeriod
    .filter(d => {
      const createdDate = getDealCreatedDate(d);
      if (!createdDate) return false;
      return isAfter(createdDate, movementPeriod.from) && !isAfter(createdDate, movementPeriod.to);
    });

  const isPastMovementPeriod = isBefore(movementPeriod.to, now);
  const sumValue = (deals: TODO[]) => deals
    .map(d => d.value)
    .reduce((acc, value) => acc + value, 0);

  const startPipelineValue = sumValue(startPipelineDeals);
  const newPipelineValue = sumValue(newPipelineDeals);
  const wonValue = sumValue(dealsExpectedInPeriod.filter(d => d.is_won));
  const lostValue = sumValue(dealsExpectedInPeriod.filter(d => d.is_lost));
  const startStillOpenValue = isPastMovementPeriod
    ? 0
    : sumValue(startPipelineDeals.filter(d => !d.is_closed));
  const newStillOpenValue = isPastMovementPeriod
    ? 0
    : sumValue(newPipelineDeals.filter(d => !d.is_closed));

  // TODO(Zabir): Replace this WIP movement model with a reviewed helper/data contract.
  // Needed fields: period-start pipeline by Revelate expected close date, new pipeline,
  // pulled-in, pushed-out, slipped, won, lost and still-open cohorts. Outcomes should be
  // attributable back to period-start vs new pipeline. Until then, won/lost use the same
  // expected-close-period basis as the "Expected to close" cards.
  return [
    {
      type: "start",
      label: "Pipeline at period start",
      value: startPipelineValue,
    },
    {
      type: "positive",
      label: "New pipeline",
      value: newPipelineValue,
    },
    {
      type: "negative",
      label: "Won",
      value: -wonValue,
    },
    {
      type: "negative",
      label: "Lost",
      value: -lostValue,
    },
    {
      type: "end",
      label: "Still open from start pipeline",
      value: startStillOpenValue,
    },
    {
      type: "end",
      label: "Still open from new pipeline",
      value: newStillOpenValue,
    },
  ];
}

const topRiskIndicatorWeights = [0.50, 0.30, 0.15, 0.05];

function getManagementReviewSourceProperty(companyId: number | null | undefined) {
  if (companyId === 49) return "source_of_deal";
  if (companyId === 13) return "conversion_source";
  return null;
}

function getManagementReviewCategory(
  deal: TODO,
  property: string | null,
) {
  if (!property) return "Not specified";

  const category = deal.provider_data?.properties?.[property];
  return typeof category === "string" && category.length
    ? category
    : "Not specified";
}

function getDealPipelineCreatedSource(
  deal: TODO,
  companyId: number | null | undefined,
) {
  return getManagementReviewCategory(
    deal,
    getManagementReviewSourceProperty(companyId),
  );
}

function getOpenStageProbability(
  pipelines: TODO[],
  pipelineId: string | null,
  stageId: string | null,
) {
  const probability = getStageProbability(
    pipelines,
    pipelineId,
    stageId,
    true,
  );

  return probability > 0 && probability < 1 ? probability : null;
}

function getOpenDealstageHistoryBeforeClose(deal: TODO, pipelines: TODO[]) {
  if (!deal.closed_at) return [];

  const closedAt = new Date(deal.closed_at);
  const dealstageHistory = deal.provider_data?.propertiesWithHistory?.dealstage ?? [];

  return [...dealstageHistory]
    .filter(record => record.timestamp && isBefore(new Date(record.timestamp), closedAt))
    .filter(record => getOpenStageProbability(
      pipelines,
      deal.pipeline,
      record.value ?? null,
    ) !== null)
    .toSorted((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

function getWeightedRiskIndicatorBreakdownForDeal(
  deal: TODO,
): Array<{ indicator: RiskIndicator; score: number; weight: number }> {
  return Object.entries(deal.risks ?? {})
    .map(([indicator, risk]) => ({
      indicator: indicator as RiskIndicator,
      signal: ((risk as { risk?: number })?.risk ?? 0) > 0
        ? (risk as { risk?: number })?.risk ?? 0
        : (risk as { score?: number })?.score ?? 0,
    }))
    .filter(indicatorRisk => indicatorRisk.signal > 0)
    .toSorted((a, b) => b.signal - a.signal)
    .slice(0, topRiskIndicatorWeights.length)
    .map((indicatorRisk, index) => ({
      indicator: indicatorRisk.indicator,
      score: indicatorRisk.signal,
      weight: topRiskIndicatorWeights[index] ?? 0,
    }));
}

function getHistoricalRiskSnapshotForLostDeal(
  deal: TODO,
  pipelines: TODO[],
): { deal: TODO; snapshotTime: Date } | null {
  if (!deal.closed_at) return null;

  const snapshotTime = new Date(new Date(deal.closed_at).getTime() - 1);
  const createdDate = getDealCreatedDate(deal);

  if (createdDate && isAfter(createdDate, snapshotTime)) return null;

  const openDealstageHistory = getOpenDealstageHistoryBeforeClose(deal, pipelines);
  const latestOpenDealstage = openDealstageHistory[0];

  if (!latestOpenDealstage?.value) return null;

  const probabilityAtSnapshotTime = getOpenStageProbability(
    pipelines,
    deal.pipeline,
    latestOpenDealstage.value,
  );

  if (probabilityAtSnapshotTime === null) return null;

  const simulatedDeal = structuredClone(deal);

  if (simulatedDeal.provider_data?.properties && simulatedDeal.provider_data?.propertiesWithHistory) {
    for (const property of Object.keys(simulatedDeal.provider_data.propertiesWithHistory)) {
      simulatedDeal.provider_data.properties[property] = getHistoricalPropertyAtSnapshotDate(
        deal,
        property,
        snapshotTime,
      );
    }

    simulatedDeal.provider_data.properties.dealstage = latestOpenDealstage.value;
    simulatedDeal.provider_data.propertiesWithHistory.dealstage = openDealstageHistory;
  }

  return {
    deal: {
      ...simulatedDeal,
      is_closed: false,
      is_won: false,
      is_lost: false,
      percentage_of_total_value: 10,
      deal_scale: 10,
      close_probability: probabilityAtSnapshotTime,
      stage_scale: probabilityAtSnapshotTime,
      stage_scale_at_snapshot_time: probabilityAtSnapshotTime,
    },
    snapshotTime,
  };
}

function getHistoricalWeightedRiskIndicatorBreakdownForLostDeal(
  deal: TODO,
  companyId: number | null | undefined,
  pipelines: TODO[],
): Array<{ indicator: RiskIndicator; score: number; weight: number }> {
  const snapshot = getHistoricalRiskSnapshotForLostDeal(deal, pipelines);
  if (!snapshot) return [];

  const [dealAtSnapshotTimeWithRiskIndicatorsEvaluated] = evaluateRiskIndicators(
    [snapshot.deal],
    companyId,
    pipelines,
    undefined,
    snapshot.snapshotTime,
  );

  if (!dealAtSnapshotTimeWithRiskIndicatorsEvaluated) return [];

  return getWeightedRiskIndicatorBreakdownForDeal(
    dealAtSnapshotTimeWithRiskIndicatorsEvaluated,
  );
}

function getHistoricalMediumHighRiskIndicatorsForLostDeal(
  deal: TODO,
  companyId: number | null | undefined,
  pipelines: TODO[],
): Array<{ indicator: RiskIndicator; score: number }> | null {
  const snapshot = getHistoricalRiskSnapshotForLostDeal(deal, pipelines);
  if (!snapshot) return null;

  const [evaluatedDeal] = evaluateRiskIndicators(
    [snapshot.deal],
    companyId,
    pipelines,
    undefined,
    snapshot.snapshotTime,
  );
  if (!evaluatedDeal) return null;

  return Object.entries(evaluatedDeal.risks ?? {})
    .map(([indicator, risk]) => {
      const riskValue = (risk as { risk?: number })?.risk ?? 0;
      const scoreValue = (risk as { score?: number })?.score ?? 0;
      return {
        indicator: indicator as RiskIndicator,
        score: riskValue > 0 ? riskValue : scoreValue,
      };
    })
    .filter(row => getRiskBucket(row.score) !== "low")
    .toSorted((a, b) => b.score - a.score);
}

function getStateOfSalesV2LossSignalGroups(
  selectedPeriodDeals: TODO[],
  companyId: number | null | undefined,
  pipelines: TODO[],
  snapshotTime: Date,
): StateOfSalesV2LossSignalGroup[] {
  const groupKeys: Array<Exclude<RiskIndicatorGroup, "portfolioRisk">> = [
    "salesExecution",
    "buyerMomentum",
    "dealProgression",
  ];
  const groups = new Map(
    groupKeys.map(group => [group, {
      group,
      label: mapRiskGroupName(group),
      affectedDealCount: 0,
      historicallyEvaluatedDealCount: 0,
      missingHistoricalDealCount: 0,
      recentLostDealCount: 0,
      deals: [] as StateOfSalesV2LossSignalGroup["deals"],
    }]),
  );
  const recentLostDeals = selectedPeriodDeals.filter(deal => {
    if (!deal.is_lost || !deal.closed_at) return false;
    const closedAt = new Date(deal.closed_at);
    return !Number.isNaN(closedAt.getTime())
      && !isBefore(closedAt, snapshotTime)
      && !isAfter(closedAt, now);
  });

  for (const group of groups.values()) {
    group.recentLostDealCount = recentLostDeals.length;
  }

  let historicallyEvaluatedDealCount = 0;
  let missingHistoricalDealCount = 0;

  for (const deal of recentLostDeals) {
    const breakdown = getHistoricalMediumHighRiskIndicatorsForLostDeal(
      deal,
      companyId,
      pipelines,
    );

    if (!breakdown) {
      missingHistoricalDealCount += 1;
      continue;
    }
    historicallyEvaluatedDealCount += 1;
    if (!breakdown.length) continue;

    for (const groupKey of groupKeys) {
      const groupBreakdown = breakdown.filter(row => (
        riskIndicatorGroupings[groupKey].includes(row.indicator)
      ));
      if (!groupBreakdown.length) continue;

      const group = groups.get(groupKey);
      if (!group) continue;
      const primaryIndicator = groupBreakdown[0]?.indicator;

      group.affectedDealCount += 1;
      group.deals.push({
        ...deal,
        lossSignalIndicators: groupBreakdown.map(row => ({
          indicator: row.indicator,
          score: row.score,
        })),
        topRisk: primaryIndicator ? { indicator: primaryIndicator } : null,
      });
    }
  }

  for (const group of groups.values()) {
    group.historicallyEvaluatedDealCount = historicallyEvaluatedDealCount;
    group.missingHistoricalDealCount = missingHistoricalDealCount;
  }

  return groupKeys.map(groupKey => groups.get(groupKey) as StateOfSalesV2LossSignalGroup);
}

function groupManagementReviewWinLossTailReasons(
  rows: ManagementReviewWinLossFlowRow[],
) {
  const lostReasonTotals = rows
    .filter(row => row.outcome === "lost")
    .reduce<Record<string, number>>((acc, row) => ({
      ...acc,
      [row.theme]: (acc[row.theme] ?? 0) + row.value,
    }), {});

  const lostReasons = Object.keys(lostReasonTotals);

  if (lostReasons.length <= 5) {
    return rows;
  }

  const topLostReasons = new Set(lostReasons
    .toSorted((a, b) => lostReasonTotals[b] - lostReasonTotals[a])
    .slice(0, 4));

  const groupedRows = new Map<string, ManagementReviewWinLossFlowRow>();

  for (const row of rows) {
    const theme = row.outcome === "lost" && !topLostReasons.has(row.theme)
      ? "Other"
      : row.theme;
    const key = `${row.source}::${row.outcome}::${theme}`;
    const current = groupedRows.get(key);

    groupedRows.set(key, {
      source: row.source,
      outcome: row.outcome,
      theme,
      value: (current?.value ?? 0) + row.value,
    });
  }

  return [...groupedRows.values()];
}

function getManagementReviewWinLossFlows(
  dealsWithRiskScores: TODO[],
  companyId: number | null | undefined,
  pipelines: TODO[],
  periodRange: ManagementReviewPeriodRange,
): ManagementReviewWinLossFlowRow[] {
  const closedOutcomeDealsInPeriod = dealsWithRiskScores
    .filter(deal => deal.is_won || deal.is_lost)
    .filter(deal => deal.closed_at && isWithinInterval(new Date(deal.closed_at), {
      start: periodRange.from,
      end: periodRange.to,
    }));
  const flowMap = new Map<string, ManagementReviewWinLossFlowRow>();

  closedOutcomeDealsInPeriod
    .forEach(deal => {
      const source = getDealPipelineCreatedSource(deal, companyId);
      const outcome = deal.is_won ? "won" : "lost";

      if (deal.is_won) {
        const theme = "Won";
        const key = `${source}::${outcome}::${theme}`;
        const current = flowMap.get(key);

        flowMap.set(key, {
          source,
          outcome,
          theme,
          value: (current?.value ?? 0) + deal.value,
        });

        return;
      }

      const riskBreakdown = getHistoricalWeightedRiskIndicatorBreakdownForLostDeal(
        deal,
        companyId,
        pipelines,
      );
      const flowRows = riskBreakdown.length
        ? riskBreakdown.map(risk => ({
          theme: mapRiskLostReasonName(risk.indicator),
          value: deal.value * risk.weight,
        }))
        : [{
          theme: "Unclassified loss",
          value: deal.value,
        }];

      flowRows.forEach(row => {
        const key = `${source}::${outcome}::${row.theme}`;
        const current = flowMap.get(key);

        flowMap.set(key, {
          source,
          outcome,
          theme: row.theme,
          value: (current?.value ?? 0) + row.value,
        });
      });
    });

  const rows = [...flowMap.values()];
  const sourceNames = new Set(rows.map(row => row.source));
  const displayRows = sourceNames.size === 1 && sourceNames.has("Not specified")
    ? rows.map(row => ({ ...row, source: "Closed deals" }))
    : rows;

  return groupManagementReviewWinLossTailReasons(displayRows)
    .toSorted((a, b) => b.value - a.value);
}

export function evaluateRiskPipelineCoverage(
  hubspotDeals: any[],
  companyId: number | null | undefined,
  wonDealsThisQuarter: any[],
  pipelines: any[],
  companyTarget: number,
  selectedPipeline: string,
  selectedOwner: string,
  pipelineCreatedBreakdown: "source" | "segment" = "source",
  attainmentBreakdown: "source" | "segment" = "source",
  periodRange?: ManagementReviewPeriodRange,
  winLossPeriodRange?: ManagementReviewPeriodRange,
) {
  const managementReviewPeriod = getManagementReviewPeriod(periodRange);

  const filteredDeals = hubspotDeals.filter(deal => {
    if (selectedPipeline !== "all" && deal.pipeline !== selectedPipeline) return false;
    if (selectedOwner !== "all" && getDealOwner(deal) === null) return false;
    if (selectedOwner !== "all" && getDealOwner(deal)?.id !== selectedOwner) return false;
    return true;
  })

  const filteredWonDealsThisQuarter = wonDealsThisQuarter.filter(deal => {
    if (selectedPipeline !== "all" && deal.pipeline !== selectedPipeline) return false;
    if (selectedOwner !== "all" && getDealOwner(deal) === null) return false;
    if (selectedOwner !== "all" && getDealOwner(deal)?.id !== selectedOwner) return false;
    return true;
  })

  const filteredWonDealsThisMonth = filteredWonDealsThisQuarter
    .filter(d => isWithinInterval(d.closed_at, {
      start: startOfThisMonth,
      end: endOfThisMonth,
    }))

  const totalWonDealValueThisMonth = filteredWonDealsThisMonth
    .map(d => d.value as number)
    .reduce((acc, val) => acc + val, 0);

  const totalWonDealValueThisQuarter = filteredWonDealsThisQuarter
    .map(d => d.value as number)
    .reduce((acc, val) => acc + val, 0);

  const totalOpenDealValue = filteredDeals
    .filter(d => d.is_won)
    .map(d => d.value)
    .reduce((acc, val) => acc + val, 0);

  const openDealsExpectedToCloseThisMonth = filteredDeals
    .filter(d => !d.is_closed && isWithinInterval(d.adjustedCloseDate, {
      start: startOfMonth(now),
      end: endOfMonth(now),
    }))

  const openDealValueExpectedToCloseThisMonth = openDealsExpectedToCloseThisMonth
    .map(d => d.value * d.stage_probability_adjusted)
    .reduce((acc, val) => acc + val, 0);

  const totalDealValueExpectedToCloseThisMonth =
    totalWonDealValueThisMonth + openDealValueExpectedToCloseThisMonth;

  // Without date-shift
  const openDealsExpectedToCloseThisQuarter = filteredDeals
    .filter(d => !d.is_closed && isWithinInterval(d.adjustedCloseDate, {
      start: managementReviewPeriod.from,
      end: managementReviewPeriod.to,
    }))

  const openDealValueExpectedToCloseThisQuarter = openDealsExpectedToCloseThisQuarter
    .map(d => d.value * d.stage_probability_adjusted)
    .reduce((acc, val) => acc + val, 0);

  const totalDealValueExpectedToCloseThisQuarter =
    totalWonDealValueThisQuarter + openDealValueExpectedToCloseThisQuarter;

  let deals = filteredDeals.map(d => {
    const dealValueAsPctOfTotalValue = totalOpenDealValue === 0
      ? 0
      : clamp(1, 10, Math.round(d.value * 100 / totalOpenDealValue));

    const stageProbability = getRevelateStageProbabilityForDeal(pipelines, d);
    return {
      ...d,
      percentage_of_total_value: 10,
      deal_scale: dealValueAsPctOfTotalValue,
      close_probability: stageProbability,
      stage_scale: stageProbability,
      // FIXME: Coming out to 1 too often
      coverage_time_factor: Math.min(1, msInAvgMonth / (d.remainingStagesTimeMs ?? 1)),
      // coverage_time_factor: (4 / 17),
    };
  })

  // Sources and segments
  const sourceProperty =
    companyId === 49
      ? "source_of_deal"
      : companyId === 13
        ? "conversion_source"
        : "";

  const segmentProperty =
    companyId === 49
      ? "segment_1"
      : companyId === 13
        ? "sales_office"
        : "";

  let pipelineCreatedByCategory: Record<string, {
    categoryTotal: number,
    totalOfAllCategories: number,
    pct: number,
  }> = {};

  // Sources and segments currently only implemented for
  // company 49 (Two) and company 13 (Tendium)
  if (companyId === 49 || companyId === 13) {

    for (const d of deals) {
      const createDate = d.provider_data?.properties?.createdate;
      if (!createDate || !isWithinInterval(createDate, {
        start: managementReviewPeriod.from,
        end: managementReviewPeriod.to,
      })) {
        continue;
      }

      let category = pipelineCreatedBreakdown === "source"
        ? d.provider_data?.properties?.[sourceProperty]
        : pipelineCreatedBreakdown === "segment"
          ? d.provider_data?.properties?.[segmentProperty]
          : null;

      category = category ?? "Not specified";
      if (!category.length) category = "Not specified";

      if (!Object.keys(pipelineCreatedByCategory).includes(category)) {
        pipelineCreatedByCategory[category] = {
          categoryTotal: d.value,
          totalOfAllCategories: 0,
          pct: 0,
        };
      } else {
        pipelineCreatedByCategory[category].categoryTotal =
          pipelineCreatedByCategory[category].categoryTotal + d.value;
      }
    }

    // Get the total across all sources
    let totalOfAllCategories = 0;
    for (const source of Object.keys(pipelineCreatedByCategory)) {
      totalOfAllCategories += pipelineCreatedByCategory[source].categoryTotal;
    }

    // Then figure out the percentages
    for (const source of Object.keys(pipelineCreatedByCategory)) {
      pipelineCreatedByCategory[source].totalOfAllCategories = totalOfAllCategories;

      const pct = totalOfAllCategories === 0
        ? 0
        : 100 * pipelineCreatedByCategory[source].categoryTotal / totalOfAllCategories;

      pipelineCreatedByCategory[source].pct = pct;
    }
  }

  let attainmentByCategory: Record<string, {
    categoryTotal: number,
    categoryWonTotal: number,
    categoryWinPct: number,
  }> = {};

  // Sources and segments currently only implemented for
  // company 49 (Two) and company 13 (Tendium)
  if (companyId === 49 || companyId === 13) {
    for (const d of deals) {
      if (!d.is_closed) {
        if (!isWithinInterval(d.adjustedCloseDate, {
          start: managementReviewPeriod.from,
          end: managementReviewPeriod.to,
        })) {
          continue;
        }
      }

      let category = attainmentBreakdown === "source"
        ? d.provider_data?.properties?.[sourceProperty]
        : attainmentBreakdown === "segment"
          ? d.provider_data?.properties?.[segmentProperty]
          : null;

      category = category ?? "Not specified";
      if (!category.length) category = "Not specified";

      const dealValue = d.value;
      const wonValue = d.is_won ? d.value : 0;

      if (!Object.keys(attainmentByCategory).includes(category)) {
        attainmentByCategory[category] = {
          categoryTotal: dealValue,
          categoryWonTotal: wonValue,
          categoryWinPct: 0,
        };
      } else {
        attainmentByCategory[category].categoryTotal = attainmentByCategory[category].categoryTotal + dealValue;
        attainmentByCategory[category].categoryWonTotal =
          attainmentByCategory[category].categoryWonTotal + wonValue;
      }
    }

    for (const segment of Object.keys(attainmentByCategory)) {
      attainmentByCategory[segment].categoryWinPct =
        attainmentByCategory[segment].categoryTotal === 0
          ? 0
          : 100 * attainmentByCategory[segment].categoryWonTotal / attainmentByCategory[segment].categoryTotal;
    }
  }

  const dealsWithRiskIndicatorsEvaluated = evaluateRiskIndicators(
    deals,
    companyId,
    pipelines,
    undefined,
  );

  const dealsWithRiskScores = dealsWithRiskIndicatorsEvaluated
    .map(d => ({
      ...d,
      risk_score: getTotalDealRiskScoreInCategory(d, "forecast"),
      weighted_risk_score: getWeightedDealRiskScoreInCategory(d, "forecast"),
    }));

  const largestDealsThisQuarter = dealsWithRiskScores
    .filter(deal => {
      if (deal.is_closed) {
        return Boolean(deal.closed_at) && isWithinInterval(new Date(deal.closed_at), {
          start: managementReviewPeriod.from,
          end: managementReviewPeriod.to,
        });
      }

      return Boolean(deal.adjustedCloseDate) && isWithinInterval(deal.adjustedCloseDate, {
        start: managementReviewPeriod.from,
        end: managementReviewPeriod.to,
      });
    })
    .sort((a, b) => b.value - a.value)
    .slice(0, 20);

  const managementReviewUrgencyRisks = getManagementReviewUrgencyRisks(dealsWithRiskScores);

  const managementReviewPipelineMovement = getManagementReviewPipelineMovement(
    dealsWithRiskScores,
    pipelines,
    managementReviewPeriod,
  );
  const managementReviewWinLossFlows = getManagementReviewWinLossFlows(
    dealsWithRiskScores,
    companyId,
    pipelines,
    winLossPeriodRange ?? managementReviewPeriod,
  );

  const totalStageScaledOpenDealValue = dealsWithRiskScores
    .filter(d => !d.is_closed)
    .filter(d => d.stage_probability > 0)
    .map(d => d.value * d.stage_probability_adjusted * d.coverage_time_factor * (100 - d.risk_score) / 100)
    .reduce((acc, val) => acc + val, 0);

  const coverage = 100 * totalStageScaledOpenDealValue / companyTarget;

  const chartData = formatDealsForPipelineCoverage(
    dealsWithRiskScores,
    companyTarget,
  );

  const earlyStage = chartData.find(row => row.close_probability === 0.2);
  const midStage = chartData.find(row => row.close_probability === 0.6);
  const lateStage = chartData.find(row => row.close_probability === 1.0);

  const isEarlyStageWeak = !earlyStage?.coverage_percentage || (earlyStage?.coverage_percentage && earlyStage.coverage_percentage < 100);
  const isMidStageWeak = !midStage?.coverage_percentage || (midStage?.coverage_percentage && midStage.coverage_percentage < 100);
  const isLateStageWeak = !lateStage?.coverage_percentage || (lateStage?.coverage_percentage && lateStage.coverage_percentage < 100);

  const numWeakStages = Number(isEarlyStageWeak) + Number(isMidStageWeak) + Number(isLateStageWeak);
  const areAllStagesStrong = numWeakStages === 0;
  const areMultipleStagesWeak = numWeakStages > 1;

  const summary = areAllStagesStrong
    ? "Pipeline coverage is sufficient across all stages. Current progression supports revenue targets."
    : areMultipleStagesWeak
      ? "Pipeline coverage is insufficient in multiple stages. Revenue is under pressure both long and short term."
      : isEarlyStageWeak
        ? "Coverage is insufficient in early stages. Increased inflow is required to sustain target attainment long-term."
        : isLateStageWeak
          ? "Coverage is insufficient in late stages. Deal progression is required to sustain expected outcomes."
          : "Pipeline coverage is insufficient in multiple stages. Revenue is under pressure both long and short term."

  const insight = areAllStagesStrong
    ? "Pipeline coverage is balanced across stages. Current structure supports sustained revenue generation."
    : areMultipleStagesWeak
      ? "Coverage is insufficient across multiple stages. Near-term and future revenue are at risk."
      : isEarlyStageWeak
        ? "Early stage coverage is insufficient. Future revenue generation is at risk."
        : isLateStageWeak
          ? "Late stage coverage is insufficient. Near-term revenue is at risk."
          : "Coverage is insufficient across multiple stages. Near-term and future revenue are at risk."

  // Snapshot
  const snapshotTime = subMonths(now, 1);

  const dealsCreatedBeforeOrAtSnapshotTimeIds = filteredDeals
    .filter(d => !isAfter(d.provider_data?.properties?.createdate ?? d.provider_data?.properties?.hs_createdate ?? "", snapshotTime))
    .map(d => d.id)

  const openDealsAtSnapshotTime = filteredDeals
    .filter(d => {
      const dealstageAtSnapshotTime = getHistoricalPropertyAtSnapshotDate(d, "dealstage", snapshotTime);
      const probabilityAtSnapshotTime = getStageProbability(pipelines, d.pipeline, dealstageAtSnapshotTime);
      const isOpen = probabilityAtSnapshotTime !== 0 && probabilityAtSnapshotTime < 1;
      return isOpen;
    })
    .filter(d => dealsCreatedBeforeOrAtSnapshotTimeIds.includes(d.id))

  // const wonDealsAtSnapshotTime = filteredDeals
  //   .filter(d => d.is_won)
  //   .filter(d => dealsCreatedBeforeOrAtSnapshotTimeIds.includes(d.id))

  const openDealsAtSnapshotTimeReadyForRiskAssessment = openDealsAtSnapshotTime.map(d => {
    // NOTE: Setting to full 10 to avoid taking this factor into account
    const dealValueAsPctOfTotalValue = 10;

    const dealstageAtSnapshotTime = getHistoricalPropertyAtSnapshotDate(d, "dealstage", snapshotTime);
    const probabilityAtSnapshotTime = getStageProbability(pipelines, d.pipeline, dealstageAtSnapshotTime, true);

    const closeDate = d.closed_at ? new Date(d.closed_at) : undefined;
    const dealstageAtCloseTime = getHistoricalPropertyAtSnapshotDate(d, "dealstage", closeDate);
    const probabilityAtCloseTime = getStageProbability(pipelines, d.pipeline, dealstageAtCloseTime ?? null, true);

    return {
      ...d,
      percentage_of_total_value: dealValueAsPctOfTotalValue,
      deal_scale: dealValueAsPctOfTotalValue,
      stage_scale_at_snapshot_time: probabilityAtSnapshotTime,
      stage_scale_at_close_time: probabilityAtCloseTime,
    };
  })

  const openDealsAtSnapshotTimeWithRiskIndicatorsEvaluated = evaluateRiskIndicators(
    openDealsAtSnapshotTimeReadyForRiskAssessment,
    companyId,
    pipelines,
    undefined,
  );

  const openDealsAtSnapshotTimeWithRiskScores = openDealsAtSnapshotTimeWithRiskIndicatorsEvaluated
    .map((d: any) => ({
      ...d,
      risk_score: getTotalDealRiskScoreInCategory(d, "pipelineRisk"),
      weighted_risk_score: getWeightedDealRiskScoreInCategory(d, "pipelineRisk"),
    }))
    .toSorted((a, b) => b.risk_score - a.risk_score);

  const totalStageScaledOpenDealValueAtSnapshotTime = openDealsAtSnapshotTimeWithRiskScores
    .filter(d => {
      if (selectedPipeline === "all") return true;
      return d.pipeline === selectedPipeline;
    })
    .filter(d => {
      if (selectedOwner === "all") return true;
      const owner = getDealOwner(d);
      return owner !== null && owner.id === selectedOwner;
    })
    .map(d => d.value * d.close_probability * d.coverage_time_factor * (100 - d.risk_score) / 100)
    .reduce((acc, val) => acc + val, 0);

  let coverageAtSnapshotTime = 100 * totalStageScaledOpenDealValueAtSnapshotTime / companyTarget;
  if (isNaN(coverageAtSnapshotTime)) {
    coverageAtSnapshotTime = coverage;
  }

  const coverageChangePct = 100 * (coverage - coverageAtSnapshotTime) / coverageAtSnapshotTime;
  const coverageChangeIsGood = coverageChangePct >= 0;

  return {
    summary,
    insight,
    pipelineCreated: Math.round(totalStageScaledOpenDealValue),
    coverage,
    coverageChangePct,
    coverageChangeIsGood,
    chartData,
    isEarlyStageWeak,
    isMidStageWeak,
    isLateStageWeak,
    totalDealValueExpectedToCloseThisMonth,
    totalWonDealValueThisMonth,
    totalDealValueExpectedToCloseThisQuarter,
    totalWonDealValueThisQuarter,
    pipelineCreatedByCategory,
    attainmentByCategory,
    largestDealsThisQuarter,
    managementReviewUrgencyRisks,
    managementReviewPipelineMovement,
    managementReviewWinLossFlows,
  };
}

export function evaluateRiskForReview(
  hubspotDeals: any[],
  companyId: number | null | undefined,
  pipelines: any[],
  dealStageAvgTimes: any[],
  withFullHistoricalRisk: boolean = false,
) {
  let deals = hubspotDeals.map(d => {
    const dealValueAsPctOfTotalValue = 10;

    const stageProbability = getRevelateStageProbabilityForDeal(pipelines, d);

    return {
      ...d,
      percentage_of_total_value: dealValueAsPctOfTotalValue,
      deal_scale: dealValueAsPctOfTotalValue,
      // FIXME: Check if needed
      close_probability: stageProbability,
      // FIXME: Check if needed
      stage_scale: stageProbability,
    };
  })

  const dealsWithRiskIndicatorsEvaluated = evaluateRiskIndicators(
    deals,
    companyId,
    pipelines,
    dealStageAvgTimes,
  );

  const dealsWithRiskScores = dealsWithRiskIndicatorsEvaluated
    .map(d => ({
      ...d,
      risk_score: getTotalDealRiskScoreInCategory(d, "forecast"),
      weighted_risk_score: getWeightedDealRiskScoreInCategory(d, "forecast"),
    }))
    .toSorted((a, b) => b.weighted_risk_score - a.weighted_risk_score)

  if (withFullHistoricalRisk) {
    let i = 0;
    for (const deal of dealsWithRiskScores) {
      let historicalChangePoints = new Map<string, string[]>();

      // Starting point is deal creation date
      // Must exist otherwise the rest of the logic doesn't work
      if (!deal.provider_data?.properties?.createdate) continue;

      const creationDate = format(deal.provider_data.properties.createdate, "yyyy-MM-dd");
      historicalChangePoints.set(creationDate, ["createdate"]);

      if (deal.provider_data?.propertiesWithHistory) {
        for (const [property, historyRecords] of Object.entries(deal.provider_data.propertiesWithHistory)) {
          for (const record of (historyRecords as any[])) {
            if (record.timestamp) {
              const date = format(record.timestamp, "yyyy-MM-dd");
              if (historicalChangePoints.has(date)) {
                historicalChangePoints.set(date, [...(historicalChangePoints.get(date) ?? []), property]);
              } else {
                historicalChangePoints.set(date, [property]);
              }
            }
          }
        }
      }


      // Ending point is present date
      const today = format(now, "yyyy-MM-dd");
      if (historicalChangePoints.has(today)) {
        historicalChangePoints.set(today, [...(historicalChangePoints.get(today) ?? []), "today"]);
      } else {
        historicalChangePoints.set(today, ["today"]);
      }

      // Sort oldest to newest
      // const sortedHistoricalChangePointDates = [...historicalChangePoints.keys()].sort((a, b) => a.localeCompare(b));

      // All dates from creation date to present date
      const allDatesSinceDealCreation = [creationDate];
      let nextDate = format(addDays(creationDate, 1), "yyyy-MM-dd");

      while (!isAfter(nextDate, today)) {
        allDatesSinceDealCreation.push(nextDate);
        nextDate = format(addDays(nextDate, 1), "yyyy-MM-dd");
      }

      const riskHistory: Array<{
        date: string;
        risk: number;
        weighted_risk: number;
        propertiesChanged: string[];
        riskIndicatorScores: Partial<Record<RiskIndicator, number>>;
      }> = [];

      for (const historicalDate of allDatesSinceDealCreation) {
        const snapshotTime = endOfDay(historicalDate);
        const simulatedDealAtSnapshotTime = buildRiskHistoryDealSnapshot(deal, snapshotTime);

        const [simulatedDealAtSnapshotTimeWithPointInTimeRisksEvaluated] = evaluateRiskIndicators(
          [simulatedDealAtSnapshotTime],
          companyId,
          pipelines,
          dealStageAvgTimes,
          snapshotTime,
        );

        const riskScoreAtSnapshotTime = getTotalDealRiskScoreInCategory(simulatedDealAtSnapshotTimeWithPointInTimeRisksEvaluated, "forecast");
        const weightedRiskScoreAtSnapshotTime = getWeightedDealRiskScoreInCategory(simulatedDealAtSnapshotTimeWithPointInTimeRisksEvaluated, "forecast");
        const riskIndicatorScores = Object.fromEntries(
          categoryIndicators.forecast.map(indicator => [
            indicator,
            simulatedDealAtSnapshotTimeWithPointInTimeRisksEvaluated.risks?.[indicator]?.risk ?? 0,
          ]),
        );

        riskHistory.push({
          date: historicalDate,
          risk: riskScoreAtSnapshotTime,
          weighted_risk: weightedRiskScoreAtSnapshotTime,
          propertiesChanged: historicalChangePoints.get(historicalDate) ?? [],
          riskIndicatorScores,
        });
      }

      // console.log(riskHistory);

      // Add new property
      dealsWithRiskScores[i].riskHistory = riskHistory;
      i++;
    }
  }

  return {
    deals: dealsWithRiskScores,
  };
}

export function getTotalDealRiskScoreInCategory(dealAtRisk: any, category: RiskCategory): number {
  return getWeightedDealRiskScoreInCategory(dealAtRisk, category);
}

/** Frozen baseline for review/rollback; active product consumers use top-four. */
export function getLegacyMeanDealRiskScoreInCategory(dealAtRisk: any, category: RiskCategory): number {
  const indicators = categoryIndicators[category];

  const total = indicators.reduce((acc, key) => {
    const riskObj = dealAtRisk.risks[key];
    return acc + (riskObj?.risk ?? 0);
  }, 0);

  return Math.round(total / indicators.length);
}

export function getWeightedDealRiskScoreInCategory(dealAtRisk: any, category: RiskCategory): number {
  const indicators = categoryIndicators[category];

  const risks = indicators
    .map((key) => dealAtRisk.risks[key]?.risk ?? 0)
    .sort((a, b) => b - a);

  const top4 = risks.slice(0, 4);

  const weights = [0.50, 0.30, 0.15, 0.05];

  const weightedSum = top4.reduce((acc, risk, idx) => {
    return acc + risk * (weights[idx] ?? 0);
  }, 0);

  return Math.round(weightedSum);
}


export function evaluateRiskIndicators(
  hubspotDeals: any[],
  companyId: number | null | undefined,
  pipelines: any[],
  dealStageAvgTimes: any[] | undefined,
  evaluationTime: string | Date = now,
) {
  const indicators = [...new Set([
    ...categoryIndicators["forecast"],
    ...categoryIndicators["pipelineRisk"],
  ])];

  let deals = hubspotDeals;

  for (const indicator of indicators) {
    deals = deals.map(d => evaluateRiskIndicator(
      d,
      companyId,
      indicator,
      pipelines,
      dealStageAvgTimes,
      evaluationTime,
    ))
  }

  return deals;
}

// TODO: Each risk indicator requires certain properties to be present
// These functions needs to check first if those properties are present
// and not evaluate a certain risk if those properties are absent
function evaluateRiskIndicator(
  hubspotDeal: any,
  companyId: number | null | undefined,
  indicator: RiskIndicator,
  pipelines: any[],
  dealStageAvgTimes: any[] | undefined,
  evaluationTime: string | Date,
) {
  switch (indicator) {
    case "dependsOnFewLargeDeals":
      return evaluateRiskDependsOnFewLargeDeals(hubspotDeal, companyId);
    case "noNextStepAssigned":
      return evaluateRiskIndicatorNoNextStepAssigned(hubspotDeal, companyId);
    case "lastActivityTooLongAgo":
      return evaluateRiskIndicatorLastActivityTooLongAgo(hubspotDeal, companyId, evaluationTime);
    case "lastModifiedDate":
      return evaluateRiskIndicatorLastModifiedDate(hubspotDeal, companyId, evaluationTime);
    case "closeDateInPast":
      return evaluateRiskIndicatorCloseDateInPast(hubspotDeal, companyId, evaluationTime);
    case "closeDateSlippage":
      return evaluateRiskCloseDateSlippage(hubspotDeal, companyId, evaluationTime);
    case "stalled":
      return evaluateRiskIndicatorStalled(hubspotDeal, companyId);
    case "numberOfMeetings":
      return evaluateRiskIndicatorNumberOfMeetings(hubspotDeal, companyId);
    case "emailResponseTime":
      return evaluateRiskEmailResponseTime(hubspotDeal, companyId, evaluationTime);
    case "emailRatio":
      return evaluateRiskEmailRatio(hubspotDeal, companyId);
    case "stageProgression":
      return evaluateRiskStageProgression(hubspotDeal, companyId, dealStageAvgTimes, evaluationTime);
    case "stageRegression":
      return evaluateRiskStageRegression(hubspotDeal, companyId, pipelines, evaluationTime);
    case "stakeholderInvolvement":
      return evaluateRiskStakeholderInvolvement(hubspotDeal, companyId);
    case "toneOfVoice":
      return evaluateRiskToneOfVoice(hubspotDeal, companyId);
    case "probabilityInflation":
      return evaluateRiskProbabilityInflation(hubspotDeal, companyId);
    default:
      return hubspotDeal;
  }
}

function evaluateRiskDependsOnFewLargeDeals(
  hubspotDeal: any,
  companyId: number | null | undefined,
) {
  const hasRisk = Object.hasOwn(hubspotDeal, "pipelineConcentration");

  const weight = getRiskWeight("dependsOnFewLargeDeals", companyId);
  let score = 0;
  let risk = 0;
  let risk_explanation = "";
  let risk_calculation = "";
  let suggested_next_move = "";

  if (hasRisk) {
    const concentrationPct = hubspotDeal.pipelineConcentration;
    score = concentrationPct <= 5
      ? 10
      : concentrationPct <= 10
        ? 8
        : concentrationPct <= 15
          ? 6
          : concentrationPct <= 20
            ? 4
            : concentrationPct <= 25
              ? 2
              : 0;

    risk = Math.round((score * weight * hubspotDeal.percentage_of_total_value) / 3);
    risk_explanation = `Part of the top ${concentrationPct}% of deals with >50% of pipeline value`;
    risk_calculation = `${score} x ${weight} x ${hubspotDeal.percentage_of_total_value} / 3`;
  } else {
    risk_explanation = `This deal does not represent a significant percentage of the pipeline value`
  }

  hubspotDeal.risks.dependsOnFewLargeDeals = {
    risk,
    risk_explanation,
    risk_calculation,
    suggested_next_move,
    score,
    weight,
    isML: false,
  };

  return hubspotDeal;
}

function evaluateRiskIndicatorNoNextStepAssigned(
  hubspotDeal: any,
  companyId: number | null | undefined,
) {
  const hasRisk =
    !hubspotDeal.is_closed &&
    !hubspotDeal.is_won &&
    hubspotDeal.provider_data.properties.hs_next_step === null;

  if (hasRisk) {
    const weight = getRiskWeight("noNextStepAssigned", companyId);
    const score = 10;

    hubspotDeal.risks.noNextStepAssigned = {
      risk: Math.round((score * weight * hubspotDeal.percentage_of_total_value) / 3),
      risk_explanation: `TBD`,
      risk_calculation: `${score} x ${weight} x ${hubspotDeal.percentage_of_total_value} / 3`,
      suggested_next_move: "",
      score,
      weight,
      isML: isRiskIndicatorWeightML("noNextStepAssigned")
    };
  } else {
    hubspotDeal.risks.noNextStepAssigned = {
      risk: 0,
      risk_explanation: "",
      risk_calculation: "",
      suggested_next_move: "",
      score: 10,
      weight: getRiskWeight("noNextStepAssigned", companyId),
      isML: isRiskIndicatorWeightML("noNextStepAssigned"),
    };
  }

  return hubspotDeal;
}

function evaluateRiskIndicatorLastActivityTooLongAgo(
  hubspotDeal: any,
  companyId: number | null | undefined,
  evaluationTime: string | Date,
) {
  const isEligible =
    !hubspotDeal.is_closed &&
    !hubspotDeal.is_won &&
    hubspotDeal.provider_data.properties.notes_last_updated !== undefined &&
    hubspotDeal.provider_data.properties.notes_last_updated !== null;

  if (!isEligible) return hubspotDeal;

  const daysAgo = differenceInCalendarDays(evaluationTime, hubspotDeal.provider_data.properties.notes_last_updated);
  const hasRisk = daysAgo > 7;

  if (hasRisk) {
    const weight = getRiskWeight("lastActivityTooLongAgo", companyId);
    const score = 10;

    hubspotDeal.risks.lastActivityTooLongAgo = {
      risk: Math.round((score * weight * hubspotDeal.percentage_of_total_value) / 3),
      risk_explanation: `Last interaction ${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`,
      risk_calculation: `${score} x ${weight} x ${hubspotDeal.percentage_of_total_value} / 3`,
      suggested_next_move: "Buyer engagement has stalled. Confirm active interest and secure a clear next step.",
      score,
      weight,
      isML: isRiskIndicatorWeightML("lastActivityTooLongAgo")
    };
  } else {
    hubspotDeal.risks.lastActivityTooLongAgo = {
      risk: 0,
      risk_explanation: "",
      risk_calculation: "",
      suggested_next_move: "",
      score: 10,
      weight: getRiskWeight("lastActivityTooLongAgo", companyId),
      isML: isRiskIndicatorWeightML("lastActivityTooLongAgo")
    };
  }

  return hubspotDeal;
}

function evaluateRiskIndicatorLastModifiedDate(
  hubspotDeal: any,
  companyId: number | null | undefined,
  evaluationTime: string | Date,
) {
  const hasRisk =
    !hubspotDeal.is_closed &&
    !hubspotDeal.is_won &&
    hubspotDeal.provider_data.properties.hs_lastmodifieddate !== undefined &&
    hubspotDeal.provider_data.properties.hs_lastmodifieddate !== null;

  if (!hasRisk) return hubspotDeal;

  const daysAgo = differenceInCalendarDays(evaluationTime, hubspotDeal.provider_data.properties.hs_lastmodifieddate);

  const weight = getRiskWeight("lastModifiedDate", companyId);
  const score = daysAgo < 3
    ? 1
    : daysAgo < 7
      ? 3
      : daysAgo < 10
        ? 5
        : daysAgo < 14
          ? 7
          : 10;

  hubspotDeal.risks.lastModifiedDate = {
    risk: Math.round((score * weight * hubspotDeal.percentage_of_total_value) / 3),
    risk_explanation: `Last modified ${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`,
    risk_calculation: `${score} x ${weight} x ${hubspotDeal.percentage_of_total_value} / 3`,
    suggested_next_move: "The deal has not been updated recently. Confirm whether buyer activity matches the CRM status and whether the sales rep is still driving the deal.",
    score,
    weight,
    isML: isRiskIndicatorWeightML("lastModifiedDate")
  };

  return hubspotDeal;
}

function evaluateRiskIndicatorCloseDateInPast(
  hubspotDeal: any,
  companyId: number | null | undefined,
  evaluationTime: string | Date,
) {
  const hasRisk =
    !hubspotDeal.is_closed &&
    hubspotDeal.provider_data.properties.closedate !== null;

  if (!hasRisk) return hubspotDeal;

  const daysAgo = differenceInCalendarDays(evaluationTime, hubspotDeal.provider_data.properties.closedate);

  const weight = getRiskWeight("closeDateInPast", companyId);
  const score = daysAgo > 20
    ? 9
    : daysAgo > 14
      ? 7
      : daysAgo > 7
        ? 5
        : daysAgo > 3
          ? 3
          : 0;

  const readableCloseDate = new Date(hubspotDeal.provider_data.properties.closedate).toLocaleDateString("sv-SV");

  hubspotDeal.risks.closeDateInPast = {
    risk: Math.round((score * weight * hubspotDeal.percentage_of_total_value) / 3),
    risk_explanation: `Close date missed on ${readableCloseDate} (${daysAgo} days ago)`,
    risk_calculation: `${score} x ${weight} x ${hubspotDeal.percentage_of_total_value} / 3`,
    suggested_next_move: "Overdue close date is a large risk factor. Reconfirm buyer commitment before keeping the deal in forecast.",
    score,
    weight,
    isML: isRiskIndicatorWeightML("closeDateInPast")
  };

  return hubspotDeal;
}

function evaluateRiskIndicatorNumberOfMeetings(
  hubspotDeal: any,
  companyId: number | null | undefined,
) {
  const isEligible =
    !hubspotDeal.is_closed &&
    hubspotDeal.provider_data.properties.hs_number_of_scheduled_meetings !== undefined &&
    hubspotDeal.provider_data.properties.hs_number_of_scheduled_meetings !== null &&
    !isNaN(Number(hubspotDeal.provider_data.properties.hs_number_of_scheduled_meetings));

  if (!isEligible) return hubspotDeal;

  const numMeetings = Number(hubspotDeal.provider_data.properties.hs_number_of_scheduled_meetings);

  const hasRisk = numMeetings > AVG_NUM_MEETINGS;

  if (hasRisk) {
    const weight = getRiskWeight("numberOfMeetings", companyId);
    const score = 10;

    hubspotDeal.risks.numberOfMeetings = {
      risk: Math.round((score * weight * hubspotDeal.percentage_of_total_value) / 3),
      risk_explanation: `${numMeetings} meetings performed against an average of ${AVG_NUM_MEETINGS}`,
      risk_calculation: `${score} x ${weight} x ${hubspotDeal.percentage_of_total_value} / 3`,
      suggested_next_move: "",
      score,
      weight,
      isML: isRiskIndicatorWeightML("numberOfMeetings")
    };
  }

  return hubspotDeal;
}

function evaluateRiskIndicatorStalled(
  hubspotDeal: any,
  companyId: number | null | undefined,
) {
  const hasRisk = hubspotDeal.provider_data.properties.hs_is_stalled === "true";

  if (hasRisk) {
    const weight = getRiskWeight("stalled", companyId);
    const score = 10;
    hubspotDeal.risks.stalled = {
      risk: Math.round((score * weight * hubspotDeal.percentage_of_total_value) / 3),
      risk_explanation: `stalled`,
      risk_calculation: `${score} x ${weight} x ${hubspotDeal.percentage_of_total_value} / 3`,
      suggested_next_move: "The deal has stalled. Validate buyer commitment and confirm whether the opportunity is still active.",
      score,
      weight,
      isML: isRiskIndicatorWeightML("stalled")
    };
  }

  return hubspotDeal;
}

function evaluateRiskEmailResponseTime(
  hubspotDeal: any,
  companyId: number | null | undefined,
  evaluationTime: string | Date,
) {
  const isEligible =
    !hubspotDeal.is_closed &&
    !hubspotDeal.is_won &&
    hubspotDeal.provider_data.properties.hs_latest_sales_email_reply_date != undefined &&
    hubspotDeal.provider_data.properties.hs_latest_sales_email_reply_date != null;

  if (!isEligible) return hubspotDeal;

  const daysAgo = differenceInCalendarDays(evaluationTime, hubspotDeal.provider_data.properties.hs_latest_sales_email_reply_date);

  const hasRisk = daysAgo > 7;

  if (hasRisk) {
    const weight = getRiskWeight("emailResponseTime", companyId);
    const score = 10;

    hubspotDeal.risks.emailResponseTime = {
      risk: Math.round((score * weight * hubspotDeal.percentage_of_total_value) / 3),
      risk_explanation: `Last email reply ${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`,
      risk_calculation: `${score} x ${weight} x ${hubspotDeal.percentage_of_total_value} / 3`,
      suggested_next_move: "Buyer response times are increasing. Re-engage key stakeholders and rebuild momentum.",
      score,
      weight,
      isML: isRiskIndicatorWeightML("emailResponseTime")
    };
  }

  return hubspotDeal;
}

function evaluateRiskEmailRatio(
  hubspotDeal: any,
  companyId: number | null | undefined,
) {
  const isEligible =
    !hubspotDeal.is_closed &&
    !hubspotDeal.is_won &&
    hubspotDeal.emails?.length;

  const companyDomain = getCompanyDomain(hubspotDeal.company_id);

  const sellerEmailCount = hubspotDeal.emails
    ?.map((e: any) => e.data?.from?.email ?? null)
    .filter((sender: string | null) => sender !== null && sender.includes(companyDomain))
    .length ?? 0;

  const buyerEmailCount = hubspotDeal.emails
    ?.map((e: any) => e.data?.from?.email ?? "")
    .filter((sender: string | null) => sender !== null && !sender.includes(companyDomain))
    .length ?? 0;

  const hasRisk = isEligible && buyerEmailCount !== 0;

  const emailRatio = hasRisk && buyerEmailCount !== 0
    ? sellerEmailCount / buyerEmailCount
    : null;

  const weight = getRiskWeight("emailRatio", companyId);
  const score = emailRatio === null
    ? 0
    : emailRatio < 1.3
      ? 1
      : emailRatio < 1.5
        ? 3
        : emailRatio < 1.8
          ? 5
          : emailRatio < 2.5
            ? 7
            : 9;

  const risk = hasRisk
    ? Math.round((score * weight * hubspotDeal.percentage_of_total_value) / 3)
    : 0;

  const risk_explanation = hasRisk && emailRatio !== null
    ? `Email ratio of ${Math.round(emailRatio * 10) / 10}`
    : '';

  hubspotDeal.risks.emailRatio = {
    risk,
    risk_explanation,
    risk_calculation: ``,
    suggested_next_move: "Buyer engagement is low. Re-establish value alignment before investing more seller time.",
    score,
    weight,
    isML: isRiskIndicatorWeightML("emailRatio")
  };

  return hubspotDeal;
}

// FIXME: Prune propertiesWithHistory according to evaluation time
function evaluateRiskCloseDateSlippage(
  hubspotDeal: any,
  companyId: number | null | undefined,
  _evaluationTime: string | Date,
) {
  const weight = getRiskWeight("closeDateSlippage", companyId);
  const closeDateHistory = hubspotDeal.provider_data?.propertiesWithHistory?.closedate ?? [];

  const numEntries = closeDateHistory?.length ?? 0;
  const numPushes = Math.max(0, (numEntries - 1));

  const score = numPushes > 3
    ? 9
    : numPushes === 3
      ? 7
      : numPushes === 2
        ? 5
        : numPushes === 1
          ? 3
          : 0;

  hubspotDeal.risks.closeDateSlippage = {
    risk: Math.round((score * weight * hubspotDeal.percentage_of_total_value) / 3),
    risk_explanation: `Close date pushed ${numPushes} time${numPushes === 1 ? "" : "s"}`,
    risk_calculation: `${score} x ${weight} x ${hubspotDeal.percentage_of_total_value} / 3`,
    suggested_next_move: "Repeated close date slippage is a strong risk indicator. Validate pain, urgency, and decision ownership. Consider closing the deal if buyer commitment cannot be confirmed.",
    score,
    weight,
    isML: isRiskIndicatorWeightML("closeDateSlippage"),
  };

  return hubspotDeal;
}

function evaluateRiskStageProgression(
  hubspotDeal: any,
  companyId: number | null | undefined,
  dealStageAvgTimes: any[] | undefined,
  evaluationTime: string | Date,
) {
  const weight = getRiskWeight("stageProgression", companyId);
  const currentStage = hubspotDeal.provider_data?.propertiesWithHistory?.dealstage?.[0] ?? null;
  const currentStageTime = currentStage && currentStage.timestamp
    ? differenceInMilliseconds(evaluationTime, currentStage.timestamp)
    : null;

  const currentStageAvgTime = dealStageAvgTimes
    ? dealStageAvgTimes.find(e => e.stage === currentStage?.value)?.milliseconds ?? null
    : null;

  const currentStageAsPctOfAvg = currentStageTime && currentStageAvgTime && currentStageAvgTime !== 0
    ? currentStageTime / currentStageAvgTime
    : null;

  const score = currentStageAsPctOfAvg === null
    ? 0
    : currentStageAsPctOfAvg > 1.7
      ? 9
      : currentStageAsPctOfAvg > 1.5
        ? 7
        : currentStageAsPctOfAvg > 1.3
          ? 5
          : currentStageAsPctOfAvg > 1.1
            ? 3
            : 0;

  const currentStageTimeDuration = currentStageTime
    ? intervalToDuration({ start: 0, end: currentStageTime })
    : null;

  const currentStageTimeDurationFormatted = currentStageTimeDuration
    ? formatDuration(currentStageTimeDuration, {
      format: ["days"]
    })
    : null;

  const risk_explanation = currentStageTime && currentStageAvgTime && currentStageAsPctOfAvg && currentStageTimeDurationFormatted
    ? `Spent ${currentStageTimeDurationFormatted} in current stage`
    : "";

  const risk_calculation = currentStageAsPctOfAvg
    ? `${score} x ${weight} x ${hubspotDeal.percentage_of_total_value} / 3`
    : "";

  hubspotDeal.risks.stageProgression = {
    risk: Math.round((score * weight * hubspotDeal.percentage_of_total_value) / 3),
    risk_explanation,
    risk_calculation,
    suggested_next_move: "Deal progression is slow. Identify the blocker and create urgency around the next decision.",
    score,
    weight,
    isML: isRiskIndicatorWeightML("stageProgression"),
  };

  return hubspotDeal;
}

// FIXME: Prune propertiesWithHistory according to evaluation time
function evaluateRiskStageRegression(
  hubspotDeal: any,
  companyId: number | null | undefined,
  pipelines: any[],
  _evaluationTime: string | Date,
) {
  const isEligible = Array.isArray(hubspotDeal.provider_data?.propertiesWithHistory?.dealstage);

  const weight = getRiskWeight("stageRegression", companyId);

  const numRegressions = isEligible
    ? findNumStageRegressions(hubspotDeal.provider_data?.propertiesWithHistory?.dealstage, pipelines)
    : null;

  const score = isEligible && numRegressions !== null
    ? numRegressions >= 3
      ? 9
      : numRegressions === 2
        ? 5
        : numRegressions === 1
          ? 3
          : 0
    : null;

  let risk = 0;
  let risk_explanation = '';
  let risk_calculation = '';

  if (isEligible && numRegressions !== null && score !== null) {
    risk = Math.round((score * weight * hubspotDeal.percentage_of_total_value) / 3);
    risk_explanation = `${numRegressions} stage regression${numRegressions === 1 ? "" : "s"}`;
    risk_calculation = `${score} * ${weight} * ${hubspotDeal.percentage_of_total_value} / 3`;
  }

  hubspotDeal.risks.stageRegression = {
    risk,
    risk_explanation,
    risk_calculation,
    suggested_next_move: "Stage regression is a major risk factor. Requalify the opportunity before progressing further.",
    score,
    weight,
    isML: isRiskIndicatorWeightML("stageRegression"),
  };

  return hubspotDeal;
}

/**
 * @not_implemented
 */
function evaluateRiskStakeholderInvolvement(
  hubspotDeal: any,
  companyId: number | null | undefined,
) {
  const weight = getRiskWeight("stakeholderInvolvement", companyId);
  const score = 0;

  hubspotDeal.risks.stakeholderInvolvement = {
    risk: Math.round((score * weight * hubspotDeal.percentage_of_total_value) / 3),
    risk_explanation: ``,
    risk_calculation: ``,
    suggested_next_move: "",
    score,
    weight,
    isML: isRiskIndicatorWeightML("stakeholderInvolvement"),
  };

  return hubspotDeal;
}

// Constants derived from backend testing and training
const SEMANTIC_RISK_MULTIPLIER = 5;
const SEMANTIC_FINAL_COMMITMENT_MULTIPLIER = 5;

function evaluateRiskToneOfVoice(
  hubspotDeal: any,
  companyId: number | null | undefined,
) {
  const isEligible =
    !hubspotDeal.is_closed &&
    !hubspotDeal.is_won &&
    hubspotDeal.emails?.length;

  const weight = getRiskWeight("toneOfVoice", companyId);
  let score = 0;

  let risk = 0;
  let risk_explanation = "";

  if (isEligible) {
    const emailsWithAnalysis = hubspotDeal.emails
      ?.filter((email: any) => email.semantic_analysis !== null);

    const analysis = emailsWithAnalysis?.[0]?.semantic_analysis ?? null;

    if (analysis) {
      const cleanedUpAnalysis = analysis.events
        ?.filter((e: any) => e.event_type === "risk_signal" || e.event_type === "buying_signal")
        ?.map((e: any) => ({
          event_type: e.event_type,
          strength: e.strength,
          role_strength: e.role_strength,
          decision_stage: e.decision_stage,
        }))

      if (cleanedUpAnalysis) {
        let semanticRisk = 0;

        for (const event of cleanedUpAnalysis) {
          let value = event.strength * event.role_strength;
          if (event.decision_stage === "final_commitment") {
            value *= SEMANTIC_FINAL_COMMITMENT_MULTIPLIER;
          }

          if (event.event_type === "buying_signal") {
            semanticRisk -= value;
          } else if (event.event_type === "risk_signal") {
            semanticRisk += value * SEMANTIC_RISK_MULTIPLIER;
          }
        }

        score = clamp(0, 10, semanticRisk);
        risk = Math.round((score * weight * hubspotDeal.percentage_of_total_value) / 3);

        if (score > 0) {
          risk_explanation = 'Risk signals identified from email conversation';
        } else {
          risk_explanation = 'No risk signals identified from email conversation';
        }
      }
    }
  }

  hubspotDeal.risks.toneOfVoice = {
    risk,
    risk_explanation,
    risk_calculation: ``,
    suggested_next_move: "Buyer communication shows hesitation. Surface the concern and address unresolved objections.",
    score,
    weight,
    isML: isRiskIndicatorWeightML("toneOfVoice"),
  };

  return hubspotDeal;
}

/**
 * @not_implemented
 */
function evaluateRiskProbabilityInflation(
  hubspotDeal: any,
  companyId: number | null | undefined,
) {
  const weight = getRiskWeight("probabilityInflation", companyId);
  const score = 0;

  hubspotDeal.risks.probabilityInflation = {
    risk: Math.round((score * weight * hubspotDeal.percentage_of_total_value) / 3),
    risk_explanation: ``,
    risk_calculation: ``,
    suggested_next_move: "",
    score,
    weight,
    isML: isRiskIndicatorWeightML("probabilityInflation"),
  };

  return hubspotDeal;
}

function simulateDealAtSnapshotTime(
  deal: any,
  snapshotTime: Date | string,
) {
  const hasHistory = !!deal.provider_data?.propertiesWithHistory;
  if (!hasHistory) return deal;

  let simulatedDealAtSnapshotTime = structuredClone(deal);

  for (const [property, historyRecords] of Object.entries(deal.provider_data.propertiesWithHistory)) {
    // History records are sorted newest to oldest
    // Find the first entry which is before snapshot time for each property
    // And set that on the deal itself for the simulation

    // Start with default value at present date as a reasonable fallback
    let propertyValueAtSnapshotTime = deal.provider_data.properties?.[property] ?? undefined;

    let i = 0;
    for (const record of (historyRecords as any[])) {
      if (record.timestamp && record.value !== undefined && (isBefore(record.timestamp, snapshotTime) || i === (historyRecords as any[]).length - 1)) {
        propertyValueAtSnapshotTime = record.value;
        break;
      }
      i++;
    }

    simulatedDealAtSnapshotTime.provider_data[property] = propertyValueAtSnapshotTime;
  }

  return simulatedDealAtSnapshotTime;
}

type PipelineRiskChartRow = {
  pipeline: string;
  pipeline_id: string;
  month: string;
  sales_rep: string;
  sales_rep_id: string;
  expected: number;
  at_risk: number;
  without_risk: number;
};

function formatDealsForPipelineRiskChart(
  deals: any[],
  breakdown: DataBreakdown,
): PipelineRiskChartRow[] {
  const grouped: Record<string, PipelineRiskChartRow> = {};

  for (const deal of deals) {
    const pipeline = deal.pipeline_label;

    // Bucket by month (YYYY-MM)
    const closed_at = new Date(deal.closed_at);
    let month = `${closed_at.getFullYear()}-${String(closed_at.getMonth() + 1).padStart(2, "0")}`;

    // NOTE: Should be handled more elegantly than brute force check like this
    if (month === "1970-01") month = "No date";

    const sales_rep = deal.owner_name ?? "No owner";

    const groupingKey = breakdown === "by_month"
      ? month
      : breakdown === "by_sales_rep"
        ? sales_rep
        : pipeline;

    if (!grouped[groupingKey]) {
      grouped[groupingKey] = {
        pipeline: deal.pipeline_label,
        pipeline_id: deal.pipeline,
        month,
        sales_rep,
        sales_rep_id: deal.ownerId,
        expected: 0,
        at_risk: 0,
        without_risk: 0,
      };
    }

    const hasRisks = deal.risk_score > 0;

    grouped[groupingKey].expected += Math.round(deal.value);

    if (hasRisks) {
      const proportionalValueAtRisk = Math.round(deal.value * deal.risk_score / 100);
      const proportionalValueWithoutRisk = deal.value - proportionalValueAtRisk;

      grouped[groupingKey].at_risk += proportionalValueAtRisk;
      grouped[groupingKey].without_risk += proportionalValueWithoutRisk;
    } else {
      grouped[groupingKey].without_risk += Math.round(deal.value);
    }
  }

  const rows = Object.values(grouped);

  if (breakdown === "by_pipeline" || breakdown === "by_sales_rep") {
    return rows.sort((a, b) => b.expected - a.expected);
  }

  if (breakdown === "by_month") {
    return rows.sort((a, b) => {
      if (a.month === "No date") return 1;
      if (b.month === "No date") return -1;
      return b.month.localeCompare(a.month);
    });
  }

  return rows;
}

type ForecastTrendChartRow = {
  month: string; // e.g. "2026-04"
  at_risk: number;
  without_risk: number;
  total: number;
  total_minus_risk: number;
  target: number;
  crm_forecast: number;
  won: number;
};

function formatDealsForForecastTrendChart(
  openDealsWithRisk: any[],
  wonDeals: any[],
  target: number,
  startOfTimePeriod: Date,
  endOfTimePeriod: Date,
  isAccumulated: boolean,
): ForecastTrendChartRow[] {
  const grouped: Record<string, ForecastTrendChartRow> = {};

  // First populate every month
  let currentDate = startOfTimePeriod;

  while (!isAfter(currentDate, endOfTimePeriod)) {
    const month = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;

    if (!grouped[month]) {
      grouped[month] = {
        month,
        at_risk: 0,
        without_risk: 0,
        total: 0,
        total_minus_risk: 0,
        target,
        crm_forecast: 0,
        won: 0,
      };
    }

    currentDate = addMonths(currentDate, 1);
  }

  for (const deal of openDealsWithRisk) {
    const dealValueCrmProbability = Math.round(deal.value * deal.stage_probability);
    const dealValueAdjustedProbability = Math.round(deal.value * deal.stage_probability_adjusted);

    const closeDate = new Date(deal.closed_at);
    const adjustedCloseDate = new Date(deal.adjustedCloseDate);

    // const createDate = new Date(deal.provider_data.createdAt);
    // const daysOpen = differenceInCalendarDays(adjustedCloseDate, createDate);
    // const monthsOpen = daysOpen / 30;
    // const dealValuePerMonth = dealValue / monthsOpen;

    // Bucket by month (YYYY-MM)
    const month = `${closeDate.getFullYear()}-${String(closeDate.getMonth() + 1).padStart(2, "0")}`;

    const crmForecast = Math.round(dealValueCrmProbability * 1);

    if (Object.keys(grouped).includes(month)) {
      grouped[month].crm_forecast += crmForecast;
    }

    const adjustedMonth = `${adjustedCloseDate.getFullYear()}-${String(adjustedCloseDate.getMonth() + 1).padStart(2, "0")}`;

    if (!Object.keys(grouped).includes(adjustedMonth)) continue;

    const hasRisks = Object.keys(deal.risks).length > 0;

    // grouped[adjustedMonth].total += dealValuePerMonth;
    grouped[adjustedMonth].total += dealValueAdjustedProbability;
    grouped[adjustedMonth].total_minus_risk += dealValueAdjustedProbability;

    if (hasRisks) {
      const proportionalValueAtRisk = Math.round(dealValueAdjustedProbability * deal.risk_score / 100);
      const proportionalValueWithoutRisk = dealValueAdjustedProbability - proportionalValueAtRisk;

      grouped[adjustedMonth].at_risk += proportionalValueAtRisk;
      grouped[adjustedMonth].total_minus_risk -= proportionalValueAtRisk;
      grouped[adjustedMonth].without_risk += proportionalValueWithoutRisk;
    } else {
      grouped[adjustedMonth].without_risk += dealValueAdjustedProbability;
    }
  }

  for (const deal of wonDeals) {
    const dealValue = Math.round(deal.value);
    const date = new Date(deal.closed_at);

    // Bucket by month (YYYY-MM)
    const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

    if (Object.keys(grouped).includes(month)) {
      grouped[month].crm_forecast += dealValue;
      grouped[month].without_risk += dealValue;
      grouped[month].total_minus_risk += dealValue;
      grouped[month].won += dealValue;
    }
  }

  // If accumulated, then go through each month and accumulate values
  if (isAccumulated) {
    let currentDate = startOfTimePeriod;
    let accumulatedAtRisk = 0;
    let accumulatedWithoutRisk = 0;
    let accumulatedTotal = 0;
    let accumulatedTotalMinusRisk = 0;
    let accumulatedTarget = 0;
    let accumulatedCrmForecast = 0;

    while (!isAfter(currentDate, endOfTimePeriod)) {
      const month = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;

      accumulatedAtRisk += grouped[month].at_risk;
      grouped[month].at_risk = accumulatedAtRisk;

      accumulatedWithoutRisk += grouped[month].without_risk;
      grouped[month].without_risk = accumulatedWithoutRisk;

      accumulatedTotal += grouped[month].total;
      grouped[month].total = accumulatedTotal;

      accumulatedTotalMinusRisk += grouped[month].total_minus_risk;
      grouped[month].total_minus_risk = accumulatedTotalMinusRisk;

      accumulatedTarget += grouped[month].target;
      grouped[month].target = accumulatedTarget;

      accumulatedCrmForecast += grouped[month].crm_forecast;
      grouped[month].crm_forecast = accumulatedCrmForecast;

      currentDate = addMonths(currentDate, 1);
    }
  }

  return Object
    .values(grouped)
    .sort((a, b) => a.month.localeCompare(b.month));
}

type PipelineCoverageChartRow = {
  close_probability: number;
  value: number;
  target: number;
  proportional_target: number;
  coverage_percentage: number;
  coverage_percentage_for_chart: number;
  width_for_chart: number;
  fill: string;
  stage: string;
};

const chartRed = "#7C2E2E";
const chartYellow = "#E0A43A";
const chartGreen = "#0D6E55";

function formatDealsForPipelineCoverage(
  deals: any[],
  companyTarget: number,
): PipelineCoverageChartRow[] {
  const grouped: Record<string, PipelineCoverageChartRow> = {};

  const STEP = 0.2;
  const MULTIPLIER = 1 / STEP;
  const scaledTarget = companyTarget * STEP;

  for (const deal of deals) {
    // Put into discrete buckets
    let close_probability_bucket =
      Math.ceil(deal.stage_probability_adjusted * MULTIPLIER) / MULTIPLIER;

    if (close_probability_bucket < STEP) close_probability_bucket = STEP;
    // if (close_probability_bucket === 1) close_probability_bucket = 0.8;

    const key = close_probability_bucket.toFixed(1);

    if (!grouped[key]) {
      grouped[key] = {
        close_probability: close_probability_bucket,
        value: 0,
        target: scaledTarget,
        proportional_target: scaledTarget / close_probability_bucket,
        coverage_percentage: 0,
        coverage_percentage_for_chart: -1,
        width_for_chart: 0,
        fill: chartRed,
        stage: "",
      };
    }

    const dealProportionalValueAtRisk = deal.value * deal.risk_score / 100;
    const dealProportionalValueWithoutRisk = deal.value - dealProportionalValueAtRisk;
    grouped[key].value += dealProportionalValueWithoutRisk * deal.coverage_time_factor;
  }

  // Ensure all buckets exist (0.2 → 1.0)
  const result: PipelineCoverageChartRow[] = [];

  // Extra line pushed for chart visualization
  result.push({
    close_probability: 0.0,
    value: 0,
    target: 0,
    proportional_target: 0,
    coverage_percentage: 0,
    coverage_percentage_for_chart: -1,
    width_for_chart: 100,
    fill: "transparent",
    stage: "Early stage",
  });


  for (let i = 1; i <= MULTIPLIER; i++) {
    const bucket = i / MULTIPLIER;
    const key = bucket.toFixed(1);
    const coverage_percentage = 100 * (grouped[key]?.value ?? 0) / (grouped[key]?.proportional_target ?? 1);

    const bucketScaledForChartWidth = bucket * 40;
    const width_for_chart = 100 - bucketScaledForChartWidth;

    const fill = coverage_percentage >= 100
      ? chartGreen
      : coverage_percentage >= 75
        ? chartYellow
        : chartRed;

    if (grouped[key]) {
      result.push({
        ...grouped[key],
        coverage_percentage,
        coverage_percentage_for_chart: coverage_percentage,
        width_for_chart,
        fill,
        stage: "",
      });
    } else {
      result.push({
        close_probability: bucket,
        value: 0,
        target: 0,
        proportional_target: 0,
        coverage_percentage: 0,
        coverage_percentage_for_chart: 0,
        width_for_chart,
        fill: chartRed,
        stage: "",
      });
    }
  }

  // Extra line pushed for chart visualization
  result.push({
    close_probability: 1.1,
    value: 0,
    target: 0,
    proportional_target: 0,
    coverage_percentage: 0,
    coverage_percentage_for_chart: -1,
    width_for_chart: 52, // Should follow the pattern
    fill: "transparent",
    stage: "Late stage",
  });

  return result;
}

function getTopRiskInCategory(
  dealsAtRisk: any[],
  category: RiskCategory
): { risk: string; value: number, pct: number, label: string } | null {
  if (!dealsAtRisk.length) return null;

  const indicatorsInCategory = categoryIndicators[category];

  const totalDealValuesByRisk: Record<string, number> = {};

  for (const deal of dealsAtRisk) {
    if (!Object.keys(deal.risks).length) continue;

    for (const indicator of indicatorsInCategory) {
      if (deal.risks[indicator]) {
        // Option A: Total deal value
        // totalDealValuesByRisk[indicator] = (totalDealValuesByRisk[indicator] || 0) + deal.value;

        // Option B: Proportional deal value
        totalDealValuesByRisk[indicator] = (totalDealValuesByRisk[indicator] || 0) + (deal.value * deal.risk_score / 100);
      }
    }
  }

  let topRisk: string | null = null;
  let maxValue = -Infinity;

  for (const [risk, value] of Object.entries(totalDealValuesByRisk)) {
    if (value > maxValue) {
      maxValue = value;
      topRisk = risk;
    }
  }

  if (!topRisk) return null;

  return {
    risk: topRisk,
    value: maxValue,
    pct: 0,
    label: "",
  };
}

export function getTop3RisksInCategory(
  dealsAtRisk: any[],
  category: RiskCategory
): Array<{
  risk: string;
  value: number;
  pct: number;
  label: string;
  deals: any[];
}> {
  if (!dealsAtRisk.length) return [];

  const indicatorsInCategory = categoryIndicators[category];

  const risks: Record<string, { score: number, deals: any[] }> = {};

  for (const deal of dealsAtRisk) {
    if (!Object.keys(deal.risks).length) continue;

    for (const indicator of indicatorsInCategory) {
      if (!deal.risks[indicator] || !deal.risks[indicator].risk) continue;

      // Proportional deal value affected by this risk
      risks[indicator] = {
        score:
          (risks[indicator]?.score || 0) +
          (deal.value * (deal.risks[indicator].risk ?? 0)) / 100,
        deals: [
          ...(risks[indicator]?.deals ?? []),
          deal,
        ]
      }
    }
  }

  const totalValue = Object.values(risks)
    .reduce((sum, risk) => sum + risk.score, 0);

  return Object.entries(risks)
    .sort((a, b) => b[1].score - a[1].score)
    .slice(0, 3)
    .map(([risk, obj]) => ({
      risk,
      value: obj.score,
      pct: totalValue ? (obj.score / totalValue) * 100 : 0,
      label: "",
      deals: [
        ...obj.deals
      ]
        .toSorted((a, b) => b.risks[risk].risk - a.risks[risk].risk)
        .slice(0, DEALS_TABLE_COUNT),
    }));
}

export function getDealTopRiskInCateogory(
  deal: any,
  category: RiskCategory
): { risk: string; value: number, pct: number, label: string } | null {
  if (!deal) return null;

  const indicatorsInCategory = categoryIndicators[category];

  let topRisk: any = null;

  if (!Object.keys(deal.risks).length) return null;

  for (const indicator of indicatorsInCategory) {
    if (deal.risks[indicator]) {
      const risk = deal.risks[indicator];
      if (topRisk === null) {
        topRisk = risk;
        topRisk.indicator = indicator;
      } else if (topRisk.risk !== undefined && risk.risk > topRisk.risk) {
        topRisk = risk;
        topRisk.indicator = indicator;
      }
    }
  }

  return topRisk;
}

// function getAggregateRiskValuesByIndicator(deals: any[]) {
//   const aggregateRisks: Record<string, number> = {};
//
//   for (const deal of deals) {
//     for (const [indicator, risk] of Object.entries(deal.risks)) {
//       if (!risk) continue;
//       aggregateRisks[indicator] ??= 0;
//       // @ts-ignore
//       aggregateRisks[indicator] += deal.value * risk.risk / 100;
//     }
//   }
//
//   return aggregateRisks;
// }

function getAggregateRiskValuesByIndicatorGroup(deals: any[]) {
  const result: Record<string, number> = {};

  // Prepare groups
  for (const groupName of Object.keys(riskIndicatorGroupings)) {
    result[groupName] = 0;
  }

  for (const deal of deals) {
    for (const [riskIndicator, risk] of Object.entries(deal.risks ?? {})) {
      if (!risk) continue;

      // find which group this risk belongs to
      const groupName = Object.entries(riskIndicatorGroupings)
        .find(([_groupName, indicatorsInGroup]) => indicatorsInGroup.includes(riskIndicator as RiskIndicator))?.[0];

      if (!groupName) continue;

      // @ts-ignore
      result[groupName] += deal.value * risk.risk / 100;
    }
  }

  return result;
}

// FIXME: Seems to not work
// function sortDealRisksDescInPlace(hubspotDeal: any) {
//   if (!hubspotDeal.risks) return hubspotDeal;
//
//   const risksArray = Object.entries(hubspotDeal.risks);
//
//   // @ts-ignore
//   risksArray.sort(([_indicatorA, riskA], [_indicatorB, riskB]) => (riskB.risk ?? 0) - (riskA.risk ?? 0));
//
//   const sortedRisks: Record<string, any> = {};
//   for (const [key, value] of risksArray) {
//     sortedRisks[key] = value;
//   }
//
//   hubspotDeal.risks = sortedRisks;
// }
